import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiPackage, 
  FiTruck, 
  FiShoppingCart, 
  FiAlertCircle,
  FiTrendingUp,
  FiMapPin,
  FiActivity,
  FiSave
} from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Line } from 'recharts';
import api from '../services/api';
import '../components/Button.css';
import './Dashboard.css';
import DashboardClampedTooltip from '../components/DashboardClampedTooltip';
import EuropeLiveVisitorsMap from '../components/EuropeLiveVisitorsMap';

const MOBILE_BREAKPOINT = 768;
/** Aantal weken vooruit op de weekgrafiek (alleen vergelijkingsdata vorig jaar). */
const WEEKLY_CHART_FORWARD_WEEKS = 4;

function addDaysToIsoDate(iso, days) {
  if (!iso) return null;
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function shiftIsoYear(iso, yearDelta) {
  if (!iso) return null;
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + yearDelta);
  return d.toISOString().split('T')[0];
}

const Dashboard = () => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  /** Op smalle schermen voorkomt dit ‘haren’-dunne staven bij veel weken */
  const mobileBarMaxSize = isMobile ? 52 : 80;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeklySales, setWeeklySales] = useState([]);
  const [weeklySalesCurrentYear, setWeeklySalesCurrentYear] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  // Standaard: dit jaar tot heden, vergelijken metzelfde periode vorig jaar
  const initDates = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), 0, 1);
    return {
      start: start.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  };
  const defaultDates = initDates();
  const initCbmDates = () => {
    const today = new Date();
    const future = new Date(today);
    future.setDate(future.getDate() + 90);
    return {
      start: today.toISOString().split('T')[0],
      end: future.toISOString().split('T')[0],
    };
  };
  const defaultCbmDates = initCbmDates();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [cbmStartDate, setCbmStartDate] = useState(defaultCbmDates.start);
  const [cbmEndDate, setCbmEndDate] = useState(defaultCbmDates.end);
  const [selectedPreset, setSelectedPreset] = useState('yeartodate');
  const [compareMode, setCompareMode] = useState('lastyear'); // 'none', 'previous', 'lastyear'
  const [compareSales, setCompareSales] = useState([]);
  const [compareSalesCurrentYear, setCompareSalesCurrentYear] = useState([]);
  const [herkomstData, setHerkomstData] = useState([]);
  const [herkomstCompareData, setHerkomstCompareData] = useState([]);
  const [herkomstVandaag, setHerkomstVandaag] = useState([]);
  const [ordersVandaag, setOrdersVandaag] = useState({ date: null, regels: [] });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  /** CBM alleen voor het smalle infoblok boven de grafieken = zelfde periode als Start-/Einddatum filters. */
  const [cbmBannerVerkocht, setCbmBannerVerkocht] = useState(null);
  const [cbmFlow, setCbmFlow] = useState({
    summary: null,
    periods: [],
    eta_prognose_per_inkooporder: [],
    granularity: 'week',
    start_date: '',
    end_date: '',
  });
  const [aiSummary, setAiSummary] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState('');
  const [cbmFlowGranularity, setCbmFlowGranularity] = useState('auto');
  const [cbmMaxOpslag, setCbmMaxOpslag] = useState('');
  const [cbmMaxOpslagSaving, setCbmMaxOpslagSaving] = useState(false);
  const [cbmLoading, setCbmLoading] = useState(false);
  /** Retouren in gekozen periode (Bol-mail vs overig); backend filter op aangemeld_at */
  const [retourSummary, setRetourSummary] = useState(undefined);
  const [liveEurope, setLiveEurope] = useState({
    points: [],
    dots: [],
    loading: true,
    error: '',
    updatedAt: null,
    windowMinutes: 5,
  });
  const [omzetWeerPrognose, setOmzetWeerPrognose] = useState(null);
  const [omzetWeerLoading, setOmzetWeerLoading] = useState(false);
  const [omzetWeerError, setOmzetWeerError] = useState('');

  const HERKOMST_COLORS = ['#3FDFCF', '#ff6b6b', '#2fb8a8', '#6c5ce7', '#fdcb6e', '#00b894', '#e17055', '#a29bfe'];

  const fmtDashboardRetourPct = (p) =>
    p == null || Number.isNaN(Number(p))
      ? '—'
      : `${Number(p).toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

  const OMZET_WEER_OPVALLEND_LABELS = {
    mooi_weer_hoge_omzet: 'Mooi weer + hoge omzet',
    hoge_omzet_slecht_weer: 'Hoge omzet, matig weer',
    mooi_weer_lage_omzet: 'Mooi weer, lage omzet',
    slecht_weer_lage_omzet: 'Slecht weer + lage omzet',
  };

  const fmtWeerSync = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString('nl-NL', {
        timeZone: 'Europe/Amsterdam',
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const fmtOmzetWeerPct = (pct) => {
    if (pct == null || Number.isNaN(Number(pct))) return '—';
    const n = Number(pct);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`;
  };

  const fetchOmzetWeerPrognose = async () => {
    setOmzetWeerLoading(true);
    setOmzetWeerError('');
    try {
      const res = await api.get('/dashboard/omzet-weer-prognose?weeks=4');
      setOmzetWeerPrognose(res.data || null);
    } catch (err) {
      setOmzetWeerPrognose(null);
      setOmzetWeerError(err.response?.data?.error || err.message || 'Prognose laden mislukt');
    } finally {
      setOmzetWeerLoading(false);
    }
  };

  const fetchCbmBannerForChartRange = async (start, end) => {
    try {
      const query = start && end ? `?start_date=${start}&end_date=${end}` : '';
      const response = await api.get(`/dashboard/cbm-verkocht${query}`);
      setCbmBannerVerkocht(response.data?.cbm_verkocht ?? 0);
    } catch (err) {
      console.error('Error fetching CBM banner (verkoopperiode):', err);
      setCbmBannerVerkocht(null);
    }
  };

  const fetchCbmFlow = async (customStart = null, customEnd = null, customGranularity = null) => {
    const start = customStart !== null ? customStart : cbmStartDate;
    const end = customEnd !== null ? customEnd : cbmEndDate;
    const gran = customGranularity !== null ? customGranularity : cbmFlowGranularity;
    try {
      const params = new URLSearchParams();
      if (start && end) {
        params.set('start_date', start);
        params.set('end_date', end);
      }
      if (gran) params.set('granularity', gran);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/dashboard/cbm-flow${query}`);
      setCbmFlow({
        summary: response.data?.summary || null,
        periods: Array.isArray(response.data?.periods) ? response.data.periods : [],
        eta_prognose_per_inkooporder: Array.isArray(response.data?.eta_prognose_per_inkooporder)
          ? response.data.eta_prognose_per_inkooporder
          : [],
        granularity: response.data?.granularity || gran || 'week',
        start_date: response.data?.start_date || start || '',
        end_date: response.data?.end_date || end || '',
      });
    } catch (err) {
      console.error('Error fetching CBM flow:', err);
      setCbmFlow({
        summary: null,
        periods: [],
        eta_prognose_per_inkooporder: [],
        granularity: cbmFlowGranularity === 'auto' ? 'week' : cbmFlowGranularity,
        start_date: start || '',
        end_date: end || '',
      });
    }
  };

  const fetchCbmMaxOpslag = async () => {
    try {
      const res = await api.get('/parameters/dashboard_cbm_max_opslag');
      setCbmMaxOpslag(res.data?.waarde || '');
    } catch (err) {
      setCbmMaxOpslag('');
    }
  };

  const saveCbmMaxOpslag = async () => {
    setCbmMaxOpslagSaving(true);
    try {
      await api.post('/parameters', {
        parameter_naam: 'dashboard_cbm_max_opslag',
        waarde: cbmMaxOpslag,
        beschrijving: 'Maximale gewenste CBM-opslagcapaciteit voor dashboard planning',
      });
      alert('CBM grenswaarde opgeslagen.');
    } catch (err) {
      alert('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setCbmMaxOpslagSaving(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchWeeklySales();
    fetchWeeklySalesCurrentYear();
    fetchHerkomst();
    fetchCbmBannerForChartRange(defaultDates.start, defaultDates.end);
    fetchCbmFlow(defaultCbmDates.start, defaultCbmDates.end);
    fetchCbmMaxOpslag();
    fetchRetourSummary();
    fetchOmzetWeerPrognose();
    if (compareMode !== 'none' && startDate && endDate) {
      fetchCompareData();
    }
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    api.get(`/dashboard/herkomst?start_date=${today}&end_date=${today}`)
      .then(res => setHerkomstVandaag(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHerkomstVandaag([]));
    api.get(`/woocommerce/sales/day/${today}`)
      .then(res => setOrdersVandaag({ date: res.data.date, regels: res.data.regels || [] }))
      .catch(() => setOrdersVandaag({ date: null, regels: [] }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLiveEurope = () => {
      api
        .get('/dashboard/live-visitors-europe?minutes=5')
        .then((res) => {
          if (cancelled) return;
          setLiveEurope({
            points: res.data.points || [],
            dots: Array.isArray(res.data.dots) ? res.data.dots : [],
            loading: false,
            error: res.data.error || '',
            updatedAt: res.data.updatedAt || null,
            windowMinutes: typeof res.data.minutes === 'number' ? res.data.minutes : 5,
          });
        })
        .catch((err) => {
          if (cancelled) return;
          setLiveEurope((prev) => ({
            ...prev,
            loading: false,
            error: err.response?.data?.error || err.message || 'Laden mislukt',
          }));
        });
    };
    loadLiveEurope();
    const interval = setInterval(loadLiveEurope, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await Promise.race([
        api.get('/dashboard/stats'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Dashboard-statistieken timeout')), 90000)
        ),
      ]);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  const buildQuery = (customStartDate = null, customEndDate = null) => {
    const start = customStartDate !== null ? customStartDate : startDate;
    const end = customEndDate !== null ? customEndDate : endDate;
    if (start && end) {
      return `?start_date=${start}&end_date=${end}`;
    }
    return '';
  };

  const fetchWeeklySales = async () => {
    try {
      const response = await api.get(`/dashboard/weekly-sales${buildQuery()}`);
      setWeeklySales(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching weekly sales:', error);
      setWeeklySales([]); // Set to empty array on error
    }
  };

  const fetchWeeklySalesCurrentYear = async () => {
    try {
      const response = await api.get(`/dashboard/weekly-sales-current-year${buildQuery()}`);
      setWeeklySalesCurrentYear(Array.isArray(response.data) ? response.data : []);
      setLoadingSales(false);
    } catch (error) {
      console.error('Error fetching weekly sales current year:', error);
      setWeeklySalesCurrentYear([]); // Set to empty array on error
      setLoadingSales(false);
    }
  };

  const fetchHerkomst = async (customStart = null, customEnd = null) => {
    try {
      const query = buildQuery(customStart, customEnd);
      const response = await api.get(`/dashboard/herkomst${query}`);
      setHerkomstData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching herkomst:', error);
      setHerkomstData([]);
    }
  };

  const fetchRetourSummary = async (customStart = null, customEnd = null) => {
    const start = customStart !== null ? customStart : startDate;
    const end = customEnd !== null ? customEnd : endDate;
    try {
      const params = new URLSearchParams();
      if (start && end) {
        params.set('start_date', start);
        params.set('end_date', end);
      }
      const q = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/dashboard/retouren-summary${q}`);
      setRetourSummary(response.data || null);
    } catch (error) {
      console.error('Error fetching retouren-summary:', error);
      setRetourSummary(null);
    }
  };

  const handleApplyRange = async () => {
    setLoadingSales(true);
    await fetchWeeklySales();
    await fetchWeeklySalesCurrentYear();
    await fetchHerkomst();
    await fetchCbmBannerForChartRange(startDate, endDate);
    await fetchRetourSummary();

    // Als vergelijkingsmodus actief is, haal ook vergelijkingsdata op
    if (compareMode !== 'none' && startDate && endDate) {
      await fetchCompareData();
    } else {
      setCompareSales([]);
      setCompareSalesCurrentYear([]);
      setHerkomstCompareData([]);
    }
  };

  const handleApplyCbmRange = async () => {
    setCbmLoading(true);
    await fetchCbmFlow();
    setCbmLoading(false);
  };

  const calculateCompareDates = (mode = null, rangeStart = null, rangeEnd = null, extendEndDays = 0) => {
    const modeToUse = mode !== null ? mode : compareMode;
    const startUse = rangeStart !== null ? rangeStart : startDate;
    const endUse = rangeEnd !== null ? rangeEnd : endDate;
    if (!startUse || !endUse || modeToUse === 'none') return null;

    const start = new Date(startUse);
    const end = new Date(endUse);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    let compareStart, compareEnd;
    
    if (modeToUse === 'previous') {
      // Zelfde periode ervoor: verschuif met de lengte van de periode
      compareEnd = new Date(start);
      compareEnd.setDate(compareEnd.getDate() - 1);
      compareStart = new Date(compareEnd);
      compareStart.setDate(compareStart.getDate() - diffDays);
    } else if (modeToUse === 'lastyear') {
      // Zelfde periode vorig jaar
      compareStart = new Date(start);
      compareStart.setFullYear(compareStart.getFullYear() - 1);
      compareEnd = new Date(end);
      compareEnd.setFullYear(compareEnd.getFullYear() - 1);
    } else {
      return null;
    }

    if (extendEndDays > 0) {
      compareEnd.setDate(compareEnd.getDate() + extendEndDays);
    }
    
    return {
      start: compareStart.toISOString().split('T')[0],
      end: compareEnd.toISOString().split('T')[0]
    };
  };

  const fetchCompareData = async (mode = null, rangeStart = null, rangeEnd = null) => {
    const modeToUse = mode !== null ? mode : compareMode;
    const compareDates = calculateCompareDates(modeToUse, rangeStart, rangeEnd);
    if (!compareDates) {
      setCompareSales([]);
      setCompareSalesCurrentYear([]);
      setHerkomstCompareData([]);
      return;
    }

    try {
      const query = `?start_date=${compareDates.start}&end_date=${compareDates.end}`;
      const weeklyCompareDates =
        modeToUse === 'lastyear'
          ? calculateCompareDates(modeToUse, rangeStart, rangeEnd, WEEKLY_CHART_FORWARD_WEEKS * 7)
          : compareDates;
      const weeklyQuery = weeklyCompareDates
        ? `?start_date=${weeklyCompareDates.start}&end_date=${weeklyCompareDates.end}`
        : query;
      const response1 = await api.get(`/dashboard/weekly-sales${weeklyQuery}`);
      setCompareSales(response1.data || []);
      const response2 = await api.get(`/dashboard/weekly-sales-current-year${weeklyQuery}`);
      setCompareSalesCurrentYear(response2.data || []);
      const response3 = await api.get(`/dashboard/herkomst${query}`);
      setHerkomstCompareData(Array.isArray(response3.data) ? response3.data : []);
    } catch (error) {
      console.error('Error fetching compare data:', error);
      setCompareSales([]);
      setCompareSalesCurrentYear([]);
      setHerkomstCompareData([]);
    }
  };

  const setDatePreset = async (preset) => {
    const today = new Date();
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);
    let start = new Date(today);

    switch (preset) {
      case 'currentweek':
        // Huidige week: maandag t/m vandaag
        const day = today.getDay();
        const toMonday = day === 0 ? 6 : day - 1;
        start.setDate(today.getDate() - toMonday);
        break;
      case 'currentmonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'currentquarter':
        const currentMonth = today.getMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        start = new Date(today.getFullYear(), quarterStartMonth, 1);
        break;
      case 'halfyear':
        start.setMonth(today.getMonth() - 6);
        break;
      case 'year':
        start.setFullYear(today.getFullYear() - 1);
        break;
      case 'yeartodate':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    start.setHours(0, 0, 0, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    
    setStartDate(startStr);
    setEndDate(endStr);
    setSelectedPreset(preset);
    
    // Automatisch toepassen wanneer preset wordt gekozen
    setLoadingSales(true);
    try {
      const query = `?start_date=${startStr}&end_date=${endStr}`;
      const response1 = await api.get(`/dashboard/weekly-sales${query}`);
      setWeeklySales(response1.data || []);
      const response2 = await api.get(`/dashboard/weekly-sales-current-year${query}`);
      setWeeklySalesCurrentYear(response2.data || []);
      await fetchHerkomst(startStr, endStr);
      await fetchCbmBannerForChartRange(startStr, endStr);
      await fetchRetourSummary(startStr, endStr);

      // Als vergelijkingsmodus actief is, haal ook vergelijkingsdata op
      if (compareMode !== 'none') {
        await fetchCompareData(compareMode, startStr, endStr);
      } else {
        setCompareSales([]);
        setCompareSalesCurrentYear([]);
        setHerkomstCompareData([]);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoadingSales(false);
    }
  };

  const calculateDateRangeDays = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getChartHeight = () => {
    const days = calculateDateRangeDays();
    const base = (() => {
      if (!days) return 400;
      if (days <= 30) return 500;
      if (days <= 90) return 450;
      if (days <= 180) return 425;
      return 400;
    })();
    // Mobiel: genoeg hoogte voor assen + dikkere staven
    return isMobile ? Math.min(Math.max(base, 300), 440) : base;
  };

  const getCbmSectionTitle = () => {
    if (cbmFlow.granularity === 'day') return 'per dag';
    if (cbmFlow.granularity === 'month') return 'per maand';
    return 'per week';
  };

  const getChartMargins = (variant = 'default') => {
    if (!isMobile) {
      return { top: 20, right: 30, left: 20, bottom: 60 };
    }
    if (variant === 'weekly') {
      if (compareMode === 'none') {
        return { top: 12, right: 28, left: 40, bottom: 98 };
      }
      return { top: 12, right: 32, left: 2, bottom: 98 };
    }
    if (variant === 'cumulative') {
      return { top: 12, right: 14, left: 46, bottom: 98 };
    }
    if (variant === 'cbm') {
      return { top: 12, right: 10, left: 46, bottom: 108 };
    }
    return { top: 16, right: 24, left: 8, bottom: 80 };
  };

  const getXAxisInterval = (dataLength) => {
    if (isMobile) {
      if (dataLength <= 6) return 0;
      return Math.max(1, Math.floor(dataLength / 4));
    }
    const days = calculateDateRangeDays();
    if (!days) return Math.floor(dataLength / 20);
    if (days <= 30) return 0;
    if (days <= 90) return Math.floor(dataLength / 15);
    if (days <= 180) return Math.floor(dataLength / 12);
    return Math.floor(dataLength / 20);
  };

  // Bereken Y-axis domain voor aantal (links)
  const getAantalDomain = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [0, 'auto'];
    
    const days = calculateDateRangeDays();
    if (!days || days > 180) {
      // Voor lange periodes, gebruik auto scaling
      return [0, 'auto'];
    }
    
    // Voor kortere periodes, bereken min/max met marge
    // 2024 vergelijkingsdata niet meenemen (aantalCompare verwijderd)
    const allAantalValues = [
      ...data.map(d => d.aantal || 0)
    ].filter(v => v > 0);
    
    if (allAantalValues.length === 0) return [0, 'auto'];
    
    const minAantal = Math.min(...allAantalValues);
    const maxAantal = Math.max(...allAantalValues);
    
    // Voeg 10% marge toe boven en onder
    const margin = Math.max((maxAantal - minAantal) * 0.1, maxAantal * 0.05);
    const domainMin = Math.max(0, minAantal - margin);
    const domainMax = maxAantal + margin;
    
    return [domainMin, domainMax];
  };

  // Bereken Y-axis domain voor omzet (rechts)
  const getOmzetDomain = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [0, 'auto'];
    
    const days = calculateDateRangeDays();
    if (!days || days > 180) {
      // Voor lange periodes, gebruik auto scaling
      return [0, 'auto'];
    }
    
    // Voor kortere periodes, bereken min/max met marge
    // 2024 vergelijkingsdata niet meenemen (omzetCompare verwijderd)
    const allOmzetValues = [
      ...data.map(d => d.omzet || 0),
      ...data.map(d => d.omzetHuidigJaar || 0),
      ...(compareMode !== 'none' ? [
        ...data.map(d => d.omzetHuidigJaarCompare || 0)
      ] : [])
    ].filter(v => v > 0);
    
    if (allOmzetValues.length === 0) return [0, 'auto'];
    
    const minOmzet = Math.min(...allOmzetValues);
    const maxOmzet = Math.max(...allOmzetValues);
    
    // Voeg 10% marge toe boven en onder
    const margin = Math.max((maxOmzet - minOmzet) * 0.1, maxOmzet * 0.05);
    const domainMin = Math.max(0, minOmzet - margin);
    const domainMax = maxOmzet + margin;
    
    return [domainMin, domainMax];
  };

  // Bereken Y-axis domain voor cumulatieve omzet
  const getCumulativeOmzetDomain = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [0, 'auto'];
    
    const days = calculateDateRangeDays();
    if (!days || days > 180) {
      // Voor lange periodes, gebruik auto scaling
      return [0, 'auto'];
    }
    
    // Voor kortere periodes, bereken min/max met marge
    // 2024 vergelijkingsdata niet meenemen (omzetCumComparePrev verwijderd)
    const allCumValues = [
      ...data.map(d => d.omzetCumPrev || 0),
      ...data.map(d => d.omzetCumCurr || 0),
      ...(compareMode !== 'none' ? [
        ...data.map(d => d.omzetCumCompareCurr || 0).filter(v => v !== null)
      ] : [])
    ].filter(v => v > 0);
    
    if (allCumValues.length === 0) return [0, 'auto'];
    
    const minCum = Math.min(...allCumValues);
    const maxCum = Math.max(...allCumValues);
    
    // Voeg 10% marge toe boven en onder
    const margin = Math.max((maxCum - minCum) * 0.1, maxCum * 0.05);
    const domainMin = Math.max(0, minCum - margin);
    const domainMax = maxCum + margin;
    
    return [domainMin, domainMax];
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  const statCards = [
    {
      title: 'Totaal Producten',
      value: stats?.total_producten || 0,
      icon: FiPackage,
      color: '#3FDFCF',
      link: '/producten'
    },
    {
      title: 'Leveranciers',
      value: stats?.total_leveranciers || 0,
      icon: FiTruck,
      color: '#f093fb',
      link: '/leveranciers'
    },
    {
      title: 'Inkooporders',
      value: stats?.total_inkooporders || 0,
      icon: FiShoppingCart,
      color: '#4facfe',
      link: '/inkoopvoorstellen?tab=inkooporders'
    },
    {
      title: 'Lage Voorraad',
      value: stats?.lage_voorraad || 0,
      icon: FiAlertCircle,
      color: '#fee140',
      link: '/inkoopvoorstellen?tab=uitverkoop'
    },
    {
      title: 'Voorraadwaarde (inkoop)',
      value: `€${(stats?.voorraad_waarde || 0).toFixed(2)}`,
      icon: FiTrendingUp,
      color: '#30cfd0',
      link: '/producten'
    },
    {
      title: 'CBM voorraad',
      value: `${(stats?.cbm_voorraad ?? 0).toFixed(1)} m³`,
      icon: FiPackage,
      color: '#0d9488',
      link: '/producten'
    }
  ];

  // Format data for chart - combine last year and current year
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  
  // Bereken vergelijkingsjaar voor labels
  const getCompareYearLabel = () => {
    if (compareMode === 'lastyear' && startDate) {
      const start = new Date(startDate);
      return start.getFullYear() - 1;
    }
    return null;
  };
  const compareYear = getCompareYearLabel();
  
  // Helper voor duidelijke labels
  const getCompareLabel = (year, isCompare) => {
    if (!isCompare) {
      return `${year}`;
    }
    if (compareMode === 'lastyear' && compareYear) {
      if (year === previousYear) {
        return `${compareYear} (vergelijkingsperiode)`;
      } else if (year === currentYear) {
        return `${previousYear} (vergelijkingsperiode)`;
      }
    } else if (compareMode === 'previous') {
      return `${year} (vorige periode)`;
    }
    return `${year} (vergelijkingsperiode)`;
  };
  
  // Helper function to round to 2 decimals
  const roundTo2Decimals = (value) => {
    const num = parseFloat(value || 0);
    return Math.round(num * 100) / 100;
  };
  
  // Helper function to format date range
  const formatDateRange = (weekStart, weekEnd) => {
    if (!weekStart || !weekEnd) return '';
    const start = new Date(weekStart);
    const end = new Date(weekEnd);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Einde van vandaag
    
    const startStr = start.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
    const endStr = end.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
    
    // Als de week nog niet is afgelopen (end datum is vandaag of in de toekomst)
    if (end >= today) {
      return ` (${startStr} - ${endStr}*)`;
    }
    
    return ` (${startStr} - ${endStr})`;
  };
  
  // Helper function to format week info for tooltip
  const formatWeekInfo = (weekLabel, weekStart, weekEnd) => {
    if (!weekStart || !weekEnd) return weekLabel;
    
    const start = new Date(weekStart);
    const end = new Date(weekEnd);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    const startStr = start.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const endStr = end.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    // Als de week nog niet is afgelopen
    if (end >= today) {
      return `${weekLabel}\n${startStr} - ${endStr} (lopende week)`;
    }
    
    return `${weekLabel}\n${startStr} - ${endStr}`;
  };
  
  // Create maps by week number for previous and current year
  const previousYearMap = {};
  if (Array.isArray(weeklySales)) {
    weeklySales.forEach(week => {
      const num = parseInt(week.week_number, 10);
      previousYearMap[num] = {
        aantal: week.totaal_aantal || 0,
        omzet: roundTo2Decimals(week.totaal_omzet || 0),
        week_start: week.week_start,
        week_end: week.week_end
      };
    });
  }

  const currentYearMap = {};
  if (Array.isArray(weeklySalesCurrentYear)) {
    weeklySalesCurrentYear.forEach(week => {
      const num = parseInt(week.week_number, 10);
      currentYearMap[num] = {
        aantal: week.totaal_aantal || 0,
        omzet: roundTo2Decimals(week.totaal_omzet || 0),
        week_start: week.week_start,
        week_end: week.week_end
      };
    });
  }

  // Helper function to check if a date is within the selected range
  const isDateInRange = (dateStr) => {
    if (!startDate || !endDate || !dateStr) return true; // Show all if no range selected
    const date = new Date(dateStr);
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Include end date fully
    return date >= start && date <= end;
  };

  // Helper function to check if a week overlaps with the selected range
  const isWeekInRange = (weekStart, weekEnd) => {
    if (!startDate || !endDate) return true; // Show all if no range selected
    if (!weekStart || !weekEnd) return false;
    
    const weekStartDate = new Date(weekStart);
    const weekEndDate = new Date(weekEnd);
    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);
    
    // Week overlaps if it starts before range ends and ends after range starts
    return weekStartDate <= rangeEnd && weekEndDate >= rangeStart;
  };

  // Collect all unique weeks that have data and are within the selected range
  // Use week_start as key to ensure chronological ordering
  const allWeeksWithData = new Map();
  
  // Add weeks from previous year data
  if (Array.isArray(weeklySales)) {
    weeklySales.forEach(week => {
      if (isWeekInRange(week.week_start, week.week_end)) {
        const key = week.week_start || week.week;
        if (!allWeeksWithData.has(key)) {
          allWeeksWithData.set(key, {
            week_number: parseInt(week.week_number, 10),
            week_start: week.week_start,
            week_end: week.week_end,
            source: 'previous'
          });
        }
      }
    });
  }
  
  // Add weeks from current year data
  if (Array.isArray(weeklySalesCurrentYear)) {
  weeklySalesCurrentYear.forEach(week => {
    if (isWeekInRange(week.week_start, week.week_end)) {
      const key = week.week_start || week.week;
      if (!allWeeksWithData.has(key)) {
        allWeeksWithData.set(key, {
          week_number: parseInt(week.week_number, 10),
          week_start: week.week_start,
          week_end: week.week_end,
          source: 'current'
        });
      } else {
        // Update if current year week is more relevant
        const existing = allWeeksWithData.get(key);
        if (existing.source === 'previous') {
          existing.week_start = week.week_start;
          existing.week_end = week.week_end;
        }
      }
    }
  });
  }
  
  // Sort weeks by week_start date (chronological order) and extract week numbers
  const sortedWeekData = Array.from(allWeeksWithData.values())
    .filter(w => w.week_start)
    .sort((a, b) => new Date(a.week_start) - new Date(b.week_start));
  
  // If date range is selected, use only weeks within range (sorted chronologically)
  // Otherwise use all weeks from 1 to 53
  const weeks = startDate && endDate && sortedWeekData.length > 0 
    ? sortedWeekData.map(w => w.week_number)
    : Array.from({ length: 53 }, (_, i) => i + 1);
  
  // Build chart data - align by week number, comparison data by index (same chronological order)
  const chartDataUnfiltered = weeks.map((weekNumber, index) => {
    const prev = previousYearMap[weekNumber] || {};
    const curr = currentYearMap[weekNumber] || {};
    
    // Voor vergelijking: gebruik index-based mapping (data komt in chronologische volgorde)
    // 2024 vergelijkingsdata niet meer tonen - alleen vergelijkingsdata voor 2026 (2025)
    let compareCurr = {};
    
    if (compareMode !== 'none' && Array.isArray(compareSalesCurrentYear) && compareSalesCurrentYear.length > 0) {
      // Alleen vergelijkingsdata voor 2026 (2025) tonen, niet voor 2025 (2024)
      if (index < compareSalesCurrentYear.length) {
        const cmp = compareSalesCurrentYear[index];
        compareCurr = cmp ? {
          aantal: cmp.totaal_aantal || 0,
          omzet: roundTo2Decimals(cmp.totaal_omzet || 0),
          week_start: cmp.week_start,
          week_end: cmp.week_end
        } : {};
      }
    }
    
    return {
      week: `W${weekNumber}`,
      weekLabel: `Week ${weekNumber}`,
      weekStart: prev.week_start || null,
      weekEnd: prev.week_end || null,
      weekStartCurrentYear: curr.week_start || null,
      weekEndCurrentYear: curr.week_end || null,
      aantal: prev.aantal || 0,
      omzet: prev.omzet || 0,
      aantalHuidigJaar: curr.aantal || 0,
      omzetHuidigJaar: curr.omzet || 0,
      // Comparison data - alleen voor 2026 (2025), niet voor 2025 (2024)
      aantalCompare: 0, // 2024 data niet tonen
      omzetCompare: 0, // 2024 data niet tonen
      aantalHuidigJaarCompare: compareCurr.aantal || 0,
      omzetHuidigJaarCompare: compareCurr.omzet || 0,
      weekStartCompare: null, // 2024 data niet tonen
      weekEndCompare: null, // 2024 data niet tonen
      weekStartCurrentYearCompare: compareCurr.week_start || null,
      weekEndCurrentYearCompare: compareCurr.week_end || null
    };
  });

  // Filter chart data to only show weeks within the selected date range
  let chartData = chartDataUnfiltered.filter(item => {
    // Include if either the previous year week or current year week is in range
    const prevInRange = isWeekInRange(item.weekStart, item.weekEnd);
    const currInRange = isWeekInRange(item.weekStartCurrentYear, item.weekEndCurrentYear);
    const comparePrevInRange = compareMode !== 'none' ? isWeekInRange(item.weekStartCompare, item.weekEndCompare) : false;
    const compareCurrInRange = compareMode !== 'none' ? isWeekInRange(item.weekStartCurrentYearCompare, item.weekEndCurrentYearCompare) : false;
    
    // Show if any of the weeks (main or comparison) are in range, or if no range is selected
    return !startDate || !endDate || prevInRange || currInRange || comparePrevInRange || compareCurrInRange;
  });
  
  // Sort chart data by date (chronological order) instead of week number
  // This ensures weeks are shown in correct order even when week numbers span across years
  chartData = chartData.sort((a, b) => {
    // Use the earliest week_start date available for sorting
    const dateA = a.weekStartCurrentYear || a.weekStart || a.weekStartCurrentYearCompare || a.weekStartCompare;
    const dateB = b.weekStartCurrentYear || b.weekStart || b.weekStartCurrentYearCompare || b.weekStartCompare;
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return new Date(dateA) - new Date(dateB);
  });

  // Vooruitblik: komende weken tonen met vergelijkingsdata vorig jaar (geen huidig-jaar data)
  if (compareMode === 'lastyear' && endDate && Array.isArray(compareSalesCurrentYear) && compareSalesCurrentYear.length > 0) {
    const chartForwardEnd = addDaysToIsoDate(endDate, WEEKLY_CHART_FORWARD_WEEKS * 7);
    const rangeEnd = new Date(endDate);
    rangeEnd.setHours(23, 59, 59, 999);
    const existingAlignedStarts = new Set(
      chartData.map((d) => d.weekStartCurrentYear).filter(Boolean)
    );
    const forwardWeeks = [];

    compareSalesCurrentYear.forEach((cmp) => {
      if (!cmp?.week_start) return;
      const alignedStart = shiftIsoYear(cmp.week_start, 1);
      const alignedEnd = shiftIsoYear(cmp.week_end, 1);
      if (!alignedStart || !alignedEnd) return;
      const alignedStartDate = new Date(alignedStart);
      if (alignedStartDate <= rangeEnd) return;
      if (alignedStartDate > new Date(chartForwardEnd)) return;
      if (existingAlignedStarts.has(alignedStart)) return;

      const weekNumber = parseInt(cmp.week_number, 10);
      forwardWeeks.push({
        week: `W${weekNumber}`,
        weekLabel: `Week ${weekNumber}`,
        weekStart: null,
        weekEnd: null,
        weekStartCurrentYear: alignedStart,
        weekEndCurrentYear: alignedEnd,
        aantal: 0,
        omzet: 0,
        aantalHuidigJaar: 0,
        omzetHuidigJaar: 0,
        aantalCompare: 0,
        omzetCompare: 0,
        aantalHuidigJaarCompare: cmp.totaal_aantal || 0,
        omzetHuidigJaarCompare: roundTo2Decimals(cmp.totaal_omzet || 0),
        weekStartCompare: null,
        weekEndCompare: null,
        weekStartCurrentYearCompare: cmp.week_start,
        weekEndCurrentYearCompare: cmp.week_end,
        isForwardPreview: true,
      });
      existingAlignedStarts.add(alignedStart);
    });

    if (forwardWeeks.length > 0) {
      chartData = [...chartData, ...forwardWeeks].sort((a, b) => {
        const dateA = a.weekStartCurrentYear || a.weekStart;
        const dateB = b.weekStartCurrentYear || b.weekStart;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      });
    }
  }

  const hasForwardPreviewWeeks = chartData.some((d) => d.isForwardPreview);

  // Cumulatieve omzet per week (vorig jaar vs huidig jaar)
  // Voor gefilterde data starten cumulatieve waarden vanaf 0 aan het begin van het bereik
  // 2024 data wordt niet getoond - alleen 2025 en 2026
  let cumPrev = 0;
  let cumCurr = 0;
  let cumCompareCurr = 0;
  
  const chartDataCumulative = chartData.map((item) => {
    cumPrev += item.omzet || 0;
    cumCurr += item.omzetHuidigJaar || 0;
    if (compareMode !== 'none') {
      // Alleen vergelijkingsdata voor 2026 (2025) tonen, niet voor 2025 (2024)
      cumCompareCurr += item.omzetHuidigJaarCompare || 0;
    }
    return {
      ...item,
      omzetCumPrev: roundTo2Decimals(cumPrev),
      omzetCumCurr: roundTo2Decimals(cumCurr),
      omzetCumComparePrev: null, // 2024 data niet tonen
      omzetCumCompareCurr: compareMode !== 'none' ? roundTo2Decimals(cumCompareCurr) : null
    };
  });


  // Orders vandaag groeperen (WooCommerce + Bol.com: alle regels uit verkoopdata)
  const ordersVandaagByOrder = (() => {
    const regels = ordersVandaag.regels || [];
    const byKey = {};
    regels.forEach(r => {
      const key = r.woocommerce_order_id != null && r.woocommerce_order_id !== '' ? String(r.woocommerce_order_id) : `regel-${r.id}`;
      if (!byKey[key]) byKey[key] = { regels: [], totaal: 0, bron: r.bron };
      byKey[key].regels.push(r);
      byKey[key].totaal += parseFloat(r.totaal_prijs) || 0;
    });
    return Object.entries(byKey).map(([id, data]) => ({ orderId: id, ...data }));
  })();

  const cbmFlowSummaryCards = cbmFlow.summary ? [
    { label: 'CBM in magazijn', value: `${Number(cbmFlow.summary.cbm_magazijn || 0).toFixed(1)} m³` },
    { label: 'CBM onderweg', value: `${Number(cbmFlow.summary.cbm_onderweg || 0).toFixed(1)} m³` },
    { label: 'Totaal beschikbaar', value: `${Number(cbmFlow.summary.cbm_totaal_beschikbaar || 0).toFixed(1)} m³` },
    { label: 'Aandeel magazijn', value: `${Number(cbmFlow.summary.aandeel_magazijn || 0).toFixed(1)}%` },
    { label: 'Aandeel onderweg', value: `${Number(cbmFlow.summary.aandeel_onderweg || 0).toFixed(1)}%` },
    { label: 'Verwachte verkoop in periode', value: `${Number(cbmFlow.summary.cbm_verkocht || 0).toFixed(1)} m³` },
    { label: 'ETA in periode', value: `${Number(cbmFlow.summary.cbm_inkomend_eta || 0).toFixed(1)} m³` },
    { label: 'Netto periode', value: `${Number(cbmFlow.summary.cbm_netto || 0).toFixed(1)} m³` },
    { label: 'Gem. verwachte verkoop per dag', value: `${Number(cbmFlow.summary.gemiddeld_verkocht_per_dag || 0).toFixed(1)} m³` },
    { label: 'Gem. verwachte verkoop per week', value: `${Number(cbmFlow.summary.gemiddeld_verkocht_per_week || 0).toFixed(1)} m³` },
    { label: 'Dekking magazijn', value: cbmFlow.summary.dekking_dagen_magazijn != null ? `${Number(cbmFlow.summary.dekking_dagen_magazijn).toFixed(1)} dagen` : 'n.v.t.' },
    { label: 'Dekking totaal beschikbaar', value: cbmFlow.summary.dekking_dagen_beschikbaar != null ? `${Number(cbmFlow.summary.dekking_dagen_beschikbaar).toFixed(1)} dagen` : 'n.v.t.' },
    { label: 'ETA komende 30 dagen', value: `${Number(cbmFlow.summary.eta_volgende_30_dagen || 0).toFixed(1)} m³` },
    { label: 'ETA komende 60 dagen', value: `${Number(cbmFlow.summary.eta_volgende_60_dagen || 0).toFixed(1)} m³` },
    { label: 'ETA komende 90 dagen', value: `${Number(cbmFlow.summary.eta_volgende_90_dagen || 0).toFixed(1)} m³` },
    { label: 'Projectie 30 dagen', value: `${Number(cbmFlow.summary.projectie_30_dagen || 0).toFixed(1)} m³` },
    { label: 'Projectie 60 dagen', value: `${Number(cbmFlow.summary.projectie_60_dagen || 0).toFixed(1)} m³` },
    { label: 'Projectie 90 dagen', value: `${Number(cbmFlow.summary.projectie_90_dagen || 0).toFixed(1)} m³` },
  ] : [];

  const cbmFlowInsights = cbmFlow.summary ? [
    cbmFlow.summary.piek_verkocht ? `Piek verwachte afvoer in de gekozen periode: ${Number(cbmFlow.summary.piek_verkocht.value || 0).toFixed(1)} m³ in ${cbmFlow.summary.piek_verkocht.label}.` : null,
    cbmFlow.summary.piek_inkomend ? `Grootste ETA-instroom: ${Number(cbmFlow.summary.piek_inkomend.value || 0).toFixed(1)} m³ in ${cbmFlow.summary.piek_inkomend.label}.` : null,
    cbmFlow.summary.eta_prognose ? `ETA-prognose: hoogste verwachte magazijnbezetting is ${Number(cbmFlow.summary.eta_prognose.projected_at_eta || 0).toFixed(1)} m³ rond ${cbmFlow.summary.eta_prognose.datum}, inclusief verwachte verkoop tot dat moment.` : null,
    cbmFlow.summary.verkoopgroei_percentage != null ? `Verkoopprognose is gebaseerd op dezelfde periode vorig jaar met ${Number(cbmFlow.summary.verkoopgroei_percentage).toFixed(1)}% groei.` : null,
    cbmFlow.summary.dekking_dagen_beschikbaar != null
      ? `Bij het huidige gemiddelde verwachte verbruik heb je met magazijn + onderweg nog ongeveer ${Number(cbmFlow.summary.dekking_dagen_beschikbaar).toFixed(1)} dagen dekking.`
      : 'Er is nog onvoldoende CBM-prognose om een betrouwbare dekking te berekenen.',
    `Verdeling beschikbaar volume: ${Number(cbmFlow.summary.aandeel_magazijn || 0).toFixed(1)}% in magazijn en ${Number(cbmFlow.summary.aandeel_onderweg || 0).toFixed(1)}% onderweg.`,
    `Projectie bij huidig tempo: over 30 dagen ${Number(cbmFlow.summary.projectie_30_dagen || 0).toFixed(1)} m³, over 60 dagen ${Number(cbmFlow.summary.projectie_60_dagen || 0).toFixed(1)} m³ en over 90 dagen ${Number(cbmFlow.summary.projectie_90_dagen || 0).toFixed(1)} m³ beschikbaar.`,
    `Netto effect in de periode: ${Number(cbmFlow.summary.cbm_netto || 0).toFixed(1)} m³ (${Number(cbmFlow.summary.cbm_inkomend_eta || 0).toFixed(1)} m³ ETA minus ${Number(cbmFlow.summary.cbm_verkocht || 0).toFixed(1)} m³ verwacht verkocht).`,
  ].filter(Boolean) : [];

  const cbmRiskLabel = cbmFlow.summary?.risico_niveau || 'laag';
  const cbmMaxOpslagNum = parseFloat(cbmMaxOpslag) || 0;
  const capaciteitNu = cbmMaxOpslagNum > 0 && cbmFlow.summary
    ? Number((cbmMaxOpslagNum - Number(cbmFlow.summary.cbm_magazijn || 0)).toFixed(1))
    : null;
  const capaciteitNaOnderweg = cbmMaxOpslagNum > 0 && cbmFlow.summary?.eta_prognose
    ? Number((cbmMaxOpslagNum - Number(cbmFlow.summary.eta_prognose.projected_at_eta || 0)).toFixed(1))
    : null;
  const capaciteit30 = cbmMaxOpslagNum > 0 && cbmFlow.summary
    ? Number((cbmMaxOpslagNum - Number(cbmFlow.summary.projectie_30_dagen || 0)).toFixed(1))
    : null;
  const capaciteit60 = cbmMaxOpslagNum > 0 && cbmFlow.summary
    ? Number((cbmMaxOpslagNum - Number(cbmFlow.summary.projectie_60_dagen || 0)).toFixed(1))
    : null;
  const capaciteit90 = cbmMaxOpslagNum > 0 && cbmFlow.summary
    ? Number((cbmMaxOpslagNum - Number(cbmFlow.summary.projectie_90_dagen || 0)).toFixed(1))
    : null;

  return (
    <div className="dashboard">
      <h1 className="page-title">Dashboard</h1>
      
      <div className="stats-grid">
        {/* Magazijn-knop: alleen zichtbaar op mobiel */}
        <Link to="/magazijn" className="stat-card stat-card-mobile-only">
          <div className="stat-icon" style={{ background: '#22c55e' }}>
            <FiMapPin />
          </div>
          <div className="stat-content">
            <h3>Magazijn</h3>
            <p className="stat-value">Picklijst</p>
          </div>
        </Link>
        {/* Attributie-knop: alleen zichtbaar op mobiel */}
        <Link to="/attributie" className="stat-card stat-card-mobile-only">
          <div className="stat-icon" style={{ background: '#6c5ce7' }}>
            <FiActivity />
          </div>
          <div className="stat-content">
            <h3>Attributie</h3>
            <p className="stat-value">Dashboard</p>
          </div>
        </Link>
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Link key={index} to={card.link} className="stat-card">
              <div className="stat-icon" style={{ background: card.color }}>
                <Icon />
              </div>
              <div className="stat-content">
                <h3>{card.title}</h3>
                <p className="stat-value">{card.value}</p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Vandaag: herkomst cirkeldiagram + orders (WooCommerce + Bol.com) openklapbaar */}
      <div className="dashboard-section vandaag-section">
        <h2 className="section-title">Vandaag</h2>
        <div className="vandaag-section-row">
          <div className="vandaag-left">
            <div className="vandaag-grid">
          <div className="vandaag-pie-wrap">
            <h3 className="vandaag-pie-title">Herkomst orders vandaag</h3>
            {herkomstVandaag.length === 0 ? (
              <p className="empty-state small">Geen orders vandaag.</p>
            ) : (
              <div className="chart-container herkomst-chart">
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 240}>
                  <PieChart>
                    <Pie
                      data={herkomstVandaag}
                      dataKey="aantal_orders"
                      nameKey="bron_label"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 62 : 80}
                      label={isMobile ? false : ({ bron_label, aantal_orders }) => {
                        const total = herkomstVandaag.reduce((s, d) => s + d.aantal_orders, 0);
                        const pct = total ? (aantal_orders / total) * 100 : 0;
                        return pct >= 10 ? `${bron_label}: ${aantal_orders}` : null;
                      }}
                      labelLine={!isMobile}
                    >
                      {herkomstVandaag.map((_, index) => (
                        <Cell key={index} fill={HERKOMST_COLORS[index % HERKOMST_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={DashboardClampedTooltip} formatter={(value, name) => [`${value} orders`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="vandaag-orders-wrap">
            <h3 className="vandaag-orders-title">Orders vandaag (WooCommerce + Bol.com)</h3>
            {ordersVandaagByOrder.length === 0 ? (
              <p className="empty-state small">Geen orders vandaag.</p>
            ) : (
              <div className="vandaag-orders-scroll">
                <div className="vandaag-orders-list">
                  {ordersVandaagByOrder.map(({ orderId, regels, totaal, bron }) => {
                    const expanded = expandedOrderId === orderId;
                    const isRegel = orderId.startsWith('regel-');
                    return (
                      <div key={orderId} className="vandaag-order-item">
                        <div
                          className="vandaag-order-header"
                          onClick={() => setExpandedOrderId(expanded ? null : orderId)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedOrderId(expanded ? null : orderId); } }}
                          aria-expanded={expanded}
                        >
                          <span className="vandaag-order-id">{isRegel ? 'Overige' : `Order #${orderId}`}</span>
                          <span className="vandaag-order-meta">
                            {!isRegel && bron && bron !== '—' && <span className="vandaag-order-bron">{bron}</span>}
                            <span className="vandaag-order-totaal">€ {totaal.toFixed(2)}</span>
                          </span>
                        </div>
                        {expanded && (
                          <div className="vandaag-order-detail">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Aantal</th>
                                  <th>Prijs</th>
                                  <th>Totaal</th>
                                  <th>Bron</th>
                                </tr>
                              </thead>
                              <tbody>
                                {regels.map((r, i) => (
                                  <tr key={i}>
                                    <td>{r.product_naam}</td>
                                    <td>{r.aantal}</td>
                                    <td>€ {(parseFloat(r.eenheidsprijs) || 0).toFixed(2)}</td>
                                    <td>€ {(parseFloat(r.totaal_prijs) || 0).toFixed(2)}</td>
                                    <td>{r.bron || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
          <div className="vandaag-map-col">
            <EuropeLiveVisitorsMap
              dots={liveEurope.dots}
              points={liveEurope.points}
              loading={liveEurope.loading}
              error={liveEurope.error}
              updatedAt={liveEurope.updatedAt}
              windowMinutes={liveEurope.windowMinutes}
            />
          </div>
        </div>
      </div>

      <div className="dashboard-filters">
        <div className="preset-buttons">
          <span className="preset-label">Snel instellen:</span>
          <select
            className="preset-dropdown"
            value={selectedPreset}
            onChange={(e) => {
              const val = e.target.value;
              if (val) setDatePreset(val);
            }}
          >
            <option value="yeartodate">Dit jaar tot heden</option>
            <option value="currentweek">Huidige week</option>
            <option value="currentmonth">Huidige maand</option>
            <option value="currentquarter">Huidig kwartaal</option>
            <option value="week">Afgelopen week</option>
            <option value="month">Afgelopen maand</option>
            <option value="quarter">Afgelopen kwartaal</option>
            <option value="halfyear">Afgelopen half jaar</option>
            <option value="year">Afgelopen jaar</option>
          </select>
        </div>
        <div className="date-inputs">
          <div className="filter-group">
            <label>Startdatum</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => { setStartDate(e.target.value); setSelectedPreset(''); }} 
            />
          </div>
          <div className="filter-group">
            <label>Einddatum</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => { setEndDate(e.target.value); setSelectedPreset(''); }} 
            />
          </div>
          <div className="filter-group">
            <label>Vergelijken met</label>
            <select 
              value={compareMode} 
              onChange={async (e) => {
                const newMode = e.target.value;
                setCompareMode(newMode);
                if (newMode !== 'none' && startDate && endDate) {
                  setLoadingSales(true);
                  try {
                    await fetchCompareData(newMode);
                  } finally {
                    setLoadingSales(false);
                  }
                } else {
                  setCompareSales([]);
                  setCompareSalesCurrentYear([]);
                  setHerkomstCompareData([]);
                }
              }}
              style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', minWidth: '200px' }}
            >
              <option value="none">Geen vergelijking</option>
              <option value="previous">Zelfde periode ervoor</option>
              <option value="lastyear">Zelfde periode vorig jaar</option>
            </select>
          </div>
          <div className="filter-group">
            <label>CBM groepering</label>
            <select
              value={cbmFlowGranularity}
              onChange={(e) => setCbmFlowGranularity(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', minWidth: '170px' }}
            >
              <option value="auto">Automatisch</option>
              <option value="day">Per dag</option>
              <option value="week">Per week</option>
              <option value="month">Per maand</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleApplyRange} disabled={loadingSales}>
            Pas toe
          </button>
          <button className="btn btn-secondary" onClick={() => { 
            setStartDate(''); 
            setEndDate(''); 
            setSelectedPreset('');
            setCompareMode('none');
            setCompareSales([]);
            setCompareSalesCurrentYear([]);
            setHerkomstCompareData([]);
            setLoadingSales(true); 
            fetchWeeklySales(); 
            fetchWeeklySalesCurrentYear(); 
            fetchHerkomst();
            fetchCbmBannerForChartRange('', '');
            fetchRetourSummary();
          }}>
            Reset
          </button>
        </div>
      </div>

      {/* CBM verwacht verkocht over dezelfde periode als de verkoopgrafieken (Start-/Einddatum) */}
      <div className="dashboard-cbm-info">
        <span className="dashboard-cbm-label">
          {startDate && endDate ? `CBM verkoopperiode (${startDate} t/m ${endDate}):` : 'CBM verkoopperiode (jaar tot nu):'}
        </span>
        <strong className="dashboard-cbm-value">
          {cbmBannerVerkocht != null ? `${Number(cbmBannerVerkocht).toFixed(1)} m³ verwacht verkocht` : '—'}
        </strong>
      </div>

      <div
        className="dashboard-retouren-summary dashboard-retouren-summary--compact"
        title="Retourstuks als percentage van verkocht (Woo-import) in de gekozen periode; geweigerde retouren niet meegeteld."
      >
        <div className="dashboard-retouren-summary-head">
          <span className="dashboard-retouren-summary-title">Retouren</span>
          {retourSummary?.start_date && retourSummary?.end_date ? (
            <span className="dashboard-retouren-summary-dates">
              {retourSummary.start_date} t/m {retourSummary.end_date}
            </span>
          ) : null}
        </div>
        {!retourSummary ? (
          <p className="empty-state small dashboard-retouren-loading">
            {retourSummary === undefined ? 'Retourdata laden…' : 'Kon retour-overzicht niet laden.'}
          </p>
        ) : (
          <div className="dashboard-retouren-pct-row">
            <div className="dashboard-retouren-pct-item">
              <span className="dashboard-retouren-pct-label">Bol</span>
              <span className="dashboard-retouren-pct-value">{fmtDashboardRetourPct(retourSummary.pct_stuks_bol)}</span>
            </div>
            <div className="dashboard-retouren-pct-item">
              <span className="dashboard-retouren-pct-label">Overig</span>
              <span className="dashboard-retouren-pct-value">{fmtDashboardRetourPct(retourSummary.pct_stuks_overig)}</span>
            </div>
            <div className="dashboard-retouren-pct-item dashboard-retouren-pct-item--totaal">
              <span className="dashboard-retouren-pct-label">Totaal</span>
              <span className="dashboard-retouren-pct-value">{fmtDashboardRetourPct(retourSummary.pct_stuks_totaal)}</span>
            </div>
          </div>
        )}
      </div>

      {/* AI-conclusie over de geselecteerde periode (omzetontwikkeling + herkomst orders) */}
      <div className="dashboard-ai-conclusie">
        <div className="dashboard-ai-conclusie-header">
          <strong className="dashboard-ai-conclusie-title">AI-conclusie periode</strong>
          <button
            type="button"
            className="btn btn-primary dashboard-ai-conclusie-btn"
            onClick={async () => {
              setAiSummaryLoading(true);
              setAiSummaryError('');
              setAiSummary('');
              try {
                // BELANGRIJK: `w.omzet` en `w.aantal` zijn NIET de data van vorig
                // jaar — ze komen uit dezelfde huidige-periode-query als
                // `w.omzetHuidigJaar` / `w.aantalHuidigJaar`. De echte vorig-jaar
                // vergelijking staat in de *Compare-velden (zie fetchCompareData).
                // We sturen die dus als `*_vorig_jaar` mee, net als de grafiek doet.
                const hasCompare = compareMode !== 'none';
                const weekData = (chartDataCumulative || []).map((w) => ({
                  week: w.week,
                  weekStart: w.weekStartCurrentYear || w.weekStart || null,
                  aantal_huidig_jaar: Number(w.aantalHuidigJaar || 0),
                  aantal_vorig_jaar: hasCompare ? Number(w.aantalHuidigJaarCompare || 0) : 0,
                  omzet_huidig_jaar: Number(w.omzetHuidigJaar || 0),
                  omzet_vorig_jaar: hasCompare ? Number(w.omzetHuidigJaarCompare || 0) : 0,
                  omzet_cum_huidig_jaar: Number(w.omzetCumCurr || 0),
                  omzet_cum_vorig_jaar: hasCompare ? Number(w.omzetCumCompareCurr || 0) : 0,
                }));
                const sum = (arr, key) =>
                  Number(arr.reduce((s, x) => s + (Number(x[key]) || 0), 0).toFixed(2));
                const totals = {
                  aantal_huidig_jaar: sum(weekData, 'aantal_huidig_jaar'),
                  aantal_vorig_jaar: hasCompare ? sum(weekData, 'aantal_vorig_jaar') : null,
                  omzet_huidig_jaar: sum(weekData, 'omzet_huidig_jaar'),
                  omzet_vorig_jaar: hasCompare ? sum(weekData, 'omzet_vorig_jaar') : null,
                };
                const mapHerkomst = (arr) =>
                  (arr || []).map((h) => ({
                    bron: h.bron_label || h.bron || 'onbekend',
                    aantal_orders: Number(h.aantal_orders || 0),
                    totaal_omzet: Number(h.totaal_omzet || 0),
                  }));
                const cbmBlock = {
                  verwacht_verkocht_in_periode_m3:
                    cbmBannerVerkocht != null ? Number(cbmBannerVerkocht) : null,
                  magazijn_m3: cbmFlow?.summary?.cbm_magazijn ?? null,
                  onderweg_m3: cbmFlow?.summary?.cbm_onderweg ?? null,
                  totaal_beschikbaar_m3: cbmFlow?.summary?.cbm_totaal_beschikbaar ?? null,
                  netto_in_periode_m3: cbmFlow?.summary?.cbm_netto ?? null,
                };
                const retourPayload =
                  retourSummary && typeof retourSummary === 'object'
                    ? {
                        periode: `${retourSummary.start_date} t/m ${retourSummary.end_date}`,
                        effectieve_start: retourSummary.effectieve_start ?? null,
                        verkocht_stuks: Number(retourSummary.verkocht_stuks ?? 0),
                        dossiers_bol: Number(retourSummary.dossiers_bol ?? 0),
                        stuks_bol: Number(retourSummary.stuks_bol ?? 0),
                        pct_stuks_bol: retourSummary.pct_stuks_bol ?? null,
                        dossiers_overig: Number(retourSummary.dossiers_overig ?? 0),
                        stuks_overig: Number(retourSummary.stuks_overig ?? 0),
                        pct_stuks_overig: retourSummary.pct_stuks_overig ?? null,
                        dossiers_totaal: Number(retourSummary.dossiers_totaal ?? 0),
                        stuks_totaal: Number(retourSummary.stuks_totaal ?? 0),
                        pct_stuks_totaal: retourSummary.pct_stuks_totaal ?? null,
                      }
                    : null;
                const res = await api.post('/dashboard/ai-summary', {
                  startDate,
                  endDate,
                  compareMode,
                  weekData,
                  totals,
                  herkomst: mapHerkomst(herkomstData),
                  herkomstCompare: mapHerkomst(herkomstCompareData),
                  cbm: cbmBlock,
                  retouren: retourPayload,
                  retouren_compare: null,
                });
                setAiSummary(String(res.data?.summary || ''));
              } catch (e) {
                setAiSummaryError(e.response?.data?.error || e.message || 'Onbekende fout');
              } finally {
                setAiSummaryLoading(false);
              }
            }}
            disabled={aiSummaryLoading}
          >
            {aiSummaryLoading ? 'Analyseren…' : aiSummary ? 'Opnieuw analyseren' : 'Genereer conclusie'}
          </button>
        </div>
        <details className="dashboard-berekening-uitleg dashboard-ai-conclusie-details">
          <summary className="dashboard-berekening-summary">
            {aiSummary ? 'Bekijk AI-analyse' : 'Uitleg'}
          </summary>
          {!aiSummary && !aiSummaryError ? (
            <p className="dashboard-ai-conclusie-placeholder" style={{ marginTop: '0.65rem' }}>
              Korte analyse van omzet, orders en herkomst op basis van de huidige filters.
            </p>
          ) : null}
          {aiSummaryError ? (
            <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>Fout: {aiSummaryError}</div>
          ) : aiSummary ? (
            <div
              className="dashboard-ai-conclusie-body"
              dangerouslySetInnerHTML={{
                __html: aiSummary
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
              }}
            />
          ) : (
            <p className="dashboard-ai-conclusie-placeholder">
              Klik op &quot;Genereer conclusie&quot; om een AI-analyse van de omzetontwikkeling en herkomst van
              orders voor deze periode te maken.
            </p>
          )}
        </details>
      </div>

      {/* Weekly Sales Chart */}
      <div className="dashboard-section">
        <h2 className="section-title">
          Verkopen per Week (Vorig jaar vs {currentYear})
          {compareMode !== 'none' && (
            <span style={{ fontSize: '1rem', color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>
              {compareMode === 'previous' ? '• Vergeleken met vorige periode' : '• Vergeleken met vorig jaar'}
              {compareMode === 'lastyear' && hasForwardPreviewWeeks
                ? ` • ${WEEKLY_CHART_FORWARD_WEEKS} weken vooruit (vorig jaar)`
                : ''}
            </span>
          )}
        </h2>
        {loadingSales ? (
          <div className="loading">Laden grafiek...</div>
        ) : chartData.length > 0 ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={getChartHeight()} debounce={50}>
              <BarChart
                data={chartData}
                margin={getChartMargins('weekly')}
                barCategoryGap={isMobile ? '18%' : '12%'}
                barGap={isMobile ? 3 : 4}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  angle={isMobile ? -90 : -45}
                  textAnchor={isMobile ? 'middle' : 'end'}
                  height={isMobile ? 72 : 100}
                  interval={getXAxisInterval(chartData.length)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  label={!isMobile ? { value: 'Aantal', angle: -90, position: 'insideLeft' } : undefined}
                  domain={getAantalDomain(chartData)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 32 : 60}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  label={!isMobile ? { value: 'Omzet (€)', angle: 90, position: 'insideRight' } : undefined}
                  domain={getOmzetDomain(chartData)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 40 : 60}
                />
                <Tooltip
                  content={DashboardClampedTooltip}
                  formatter={(value, name, props) => {
                    const payload = props.payload;
                    const compareLabel = compareMode === 'previous' ? ' (vorige periode)' : compareMode === 'lastyear' ? ' (vorig jaar)' : '';
                    
                    if (name === 'aantal') {
                      const dateRange = formatDateRange(payload.weekStart, payload.weekEnd);
                      return [`${value} stuks`, `Aantal${dateRange}`];
                    }
                    // 2024 vergelijkingsdata niet tonen (aantalCompare en omzetCompare verwijderd)
                    if (name === 'omzet') {
                      const numValue = parseFloat(value || 0);
                      if (isNaN(numValue)) return ['€0,00', `Omzet ${previousYear}`];
                      const rounded = Math.round(numValue * 100) / 100;
                      const dateRange = formatDateRange(payload.weekStart, payload.weekEnd);
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Omzet ${previousYear}${dateRange}`];
                    }
                    if (name === 'omzetHuidigJaar') {
                      const numValue = parseFloat(value || 0);
                      if (isNaN(numValue) || value === null) return null;
                      const rounded = Math.round(numValue * 100) / 100;
                      const dateRange = formatDateRange(payload.weekStartCurrentYear, payload.weekEndCurrentYear);
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Omzet ${currentYear}${dateRange}`];
                    }
                    if (name === 'omzetHuidigJaarCompare') {
                      const numValue = parseFloat(value || 0);
                      if (isNaN(numValue) || value === null) return null;
                      const rounded = Math.round(numValue * 100) / 100;
                      const dateRange = formatDateRange(payload.weekStartCurrentYearCompare, payload.weekEndCurrentYearCompare);
                      const forwardHint = payload.isForwardPreview ? ' (vorig jaar, komende week)' : '';
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Omzet ${currentYear}${compareLabel}${dateRange}${forwardHint}`];
                    }
                    if (name === 'omzetHuidigJaar' && payload?.isForwardPreview) {
                      return null;
                    }
                    return value;
                  }}
                  labelFormatter={(label) => {
                    const data = chartData.find(d => d.week === label);
                    if (data) {
                      // Gebruik weekStartCurrentYear als primaire bron (huidig jaar), anders weekStart
                      const weekStart = data.weekStartCurrentYear || data.weekStart;
                      const weekEnd = data.weekEndCurrentYear || data.weekEnd;
                      
                      if (weekStart && weekEnd) {
                        const base = formatWeekInfo(label, weekStart, weekEnd);
                        return data.isForwardPreview ? `${base}\n(vooruitblik: vorig jaar)` : base;
                      }
                    }
                    return label;
                  }}
                />
                <Legend 
                  formatter={isMobile ? (value) => {
                    if (value.includes('vergelijkingsperiode')) return value.replace(/ \(vergelijkingsperiode\) \(€\)$/, '').replace(/ \(vorige periode\) \(€\)$/, '');
                    return value;
                  } : undefined}
                  wrapperStyle={isMobile ? { fontSize: '11px' } : undefined}
                />
                {/* Als vergelijkingsmodus actief is, toon alleen vergelijkingsdata en huidig jaar (niet de dubbele 2025 kolom) */}
                {compareMode === 'none' && (
                  <>
                    <Bar yAxisId="left" dataKey="aantal" fill="#3FDFCF" name={`Aantal ${previousYear}`} maxBarSize={mobileBarMaxSize} />
                    <Bar yAxisId="right" dataKey="omzet" fill="#2fb8a8" name={`Omzet ${previousYear} (€)`} maxBarSize={mobileBarMaxSize} />
                  </>
                )}
                {compareMode !== 'none' && (
                  <Bar 
                    yAxisId="right" 
                    dataKey="omzetHuidigJaarCompare" 
                    fill="#cc5555" 
                    name={compareMode === 'lastyear' && compareYear
                      ? `Omzet ${previousYear} (vergelijkingsperiode) (€)`
                      : `Omzet ${currentYear}${compareMode === 'previous' ? ' (vorige periode)' : ' (vergelijkingsperiode)'} (€)`
                    }
                    maxBarSize={mobileBarMaxSize}
                  />
                )}
                {/* Huidig jaar altijd als laatste */}
                <Bar yAxisId="right" dataKey="omzetHuidigJaar" fill="#ff6b6b" name={`Omzet ${currentYear} (€)`} maxBarSize={mobileBarMaxSize} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="empty-state">Geen verkoopdata beschikbaar voor het afgelopen jaar</div>
        )}
      </div>

      {/* Omzetprognose + weer */}
      <div className="dashboard-section dashboard-omzet-weer-section">
        <h2 className="section-title">
          Verwachte omzet komende weken
          <span style={{ fontSize: '1rem', color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>
            • weer + historische omzet
          </span>
        </h2>
        <details className="dashboard-berekening-uitleg dashboard-omzet-weer-uitleg">
          <summary className="dashboard-berekening-summary">Hoe werkt deze prognose?</summary>
          <ul>
            <li><strong>Baseline:</strong> omzet dezelfde week vorig jaar × verkoopgroei (parameter Inkoopvoorstellen).</li>
            <li><strong>Weer:</strong> historisch archief + verwachting via Open-Meteo (NL, gratis), max. 1× per dag opgehaald.</li>
            <li><strong>Weerfactor:</strong> geleerd uit correlatie zonuren, temperatuur en neerslag met wekelijkse omzet (schaduwdoeken).</li>
            <li><strong>Historisch:</strong> ISO-weeknummering (W01–W53, maandag = weekstart), gelijk aan de omzetgrafiek; opvallende combinaties worden gemarkeerd.</li>
            <li><strong>vs mediaan:</strong> vergelijking met de typische weekomzet over alle weken in de dataset — niet met vorig jaar (dat staat in de grafiek).</li>
            <li>Correlaties worden steeds beter naarmate er meer weken/weerdata in de database staan.</li>
          </ul>
        </details>
        {omzetWeerLoading ? (
          <div className="loading">Weer en prognose laden…</div>
        ) : omzetWeerError ? (
          <p className="dashboard-omzet-weer-error">
            {omzetWeerError}{' '}
            <button type="button" className="magazijn-refresh-link" onClick={fetchOmzetWeerPrognose}>
              Opnieuw
            </button>
          </p>
        ) : omzetWeerPrognose?.weken?.length || omzetWeerPrognose?.historie?.weken?.length ? (
          <>
            {omzetWeerPrognose.model ? (
              <p className="dashboard-omzet-weer-meta muted">
                {omzetWeerPrognose.model.weken_geleerd} weken geleerd
                {omzetWeerPrognose.model.correlaties?.zonuren != null
                  ? ` · correlatie zon: ${omzetWeerPrognose.model.correlaties.zonuren}`
                  : ''}
                {omzetWeerPrognose.model.correlaties?.temperatuur != null
                  ? ` · temp: ${omzetWeerPrognose.model.correlaties.temperatuur}`
                  : ''}
                {omzetWeerPrognose.model.correlaties?.neerslag != null
                  ? ` · regen: ${omzetWeerPrognose.model.correlaties.neerslag}`
                  : ''}
                {omzetWeerPrognose.locatie?.naam ? ` · ${omzetWeerPrognose.locatie.naam}` : ''}
                {omzetWeerPrognose.laatste_weer_sync
                  ? ` · weer bijgewerkt: ${fmtWeerSync(omzetWeerPrognose.laatste_weer_sync)}`
                  : ''}
                {omzetWeerPrognose.weer_sync?.skipped_reason === 'al_bijgewerkt_vandaag'
                  ? ' (max. 1×/dag Open-Meteo)'
                  : ''}
              </p>
            ) : null}
            {omzetWeerPrognose.weer_sync?.api_error ? (
              <p className="dashboard-omzet-weer-warn">
                Open-Meteo tijdelijk niet bereikbaar ({omzetWeerPrognose.weer_sync.api_error}). We tonen opgeslagen weerdata.
              </p>
            ) : null}
            {omzetWeerPrognose.weken?.length ? (
              <>
                <h3 className="dashboard-omzet-weer-subtitle">Prognose komende weken</h3>
                <p className="dashboard-omzet-weer-hint muted">
                  Weer uit Open-Meteo voor <strong>Nederland (centraal)</strong>, niet jouw exacte woonplaats.
                  Verwachting max. {omzetWeerPrognose.model?.verwachting_dagen_vooruit ?? 16} dagen vooruit — daarna weerfactor ×1,00 (neutraal).
                  Dit is niet &quot;deze week&quot; in je weer-app, maar de <strong>volgende kalenderweken</strong> in de tabel.
                </p>
                <div className="dashboard-omzet-weer-table-wrap">
                  <table className="dashboard-omzet-weer-table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th className="col-num">Baseline</th>
                        <th className="col-num">Weerfactor</th>
                        <th className="col-num">Verwacht</th>
                        <th className="col-num">Zon (u)</th>
                        <th className="col-num">Temp °C</th>
                        <th className="col-num">Regen mm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {omzetWeerPrognose.weken.map((w) => (
                        <tr key={w.week_start}>
                          <td>
                            <strong>{w.week_label}</strong>
                            <span className="dashboard-omzet-weer-datum"> {w.week_start}</span>
                          </td>
                          <td className="col-num">€ {(w.baseline_omzet ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="col-num">{w.weer_factor != null ? `×${Number(w.weer_factor).toFixed(2)}` : '—'}</td>
                          <td className="col-num dashboard-omzet-weer-verwacht">
                            € {(w.verwachte_omzet ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="col-num">{w.weer?.zonuren ?? '—'}</td>
                          <td className="col-num">{w.weer?.temp_gem ?? '—'}</td>
                          <td className="col-num">
                            {w.weer?.neerslag_mm ?? '—'}
                            {w.weer_verwachting_beschikbaar === false ? (
                              <span className="dashboard-omzet-weer-geen-verwachting" title={w.weer_uitleg}>
                                {' '}geen verwachting
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
            {omzetWeerPrognose.historie?.weken?.length ? (
              <details className="dashboard-berekening-uitleg dashboard-omzet-weer-historie-details">
                <summary className="dashboard-berekening-summary">
                  Historisch weer vs omzet ({omzetWeerPrognose.historie.weken.length} weken)
                </summary>
                {(() => {
                  const comboData = [...omzetWeerPrognose.historie.weken]
                    .filter((w) => Number(w.omzet) > 0)
                    .sort((a, b) => a.week_start.localeCompare(b.week_start))
                    .map((w) => ({
                      label: w.week_label,
                      jaar: w.jaar,
                      week_start: w.week_start,
                      week_end: w.week_end,
                      omzet: Number(w.omzet),
                      zonuren: w.zonuren != null ? Number(w.zonuren) : null,
                      temp: w.temp_gem != null ? Number(w.temp_gem) : null,
                      regen: w.neerslag_mm != null ? Number(w.neerslag_mm) : null,
                    }));
                  return comboData.length >= 6 ? (
                    <div className="dashboard-omzet-weer-combo-wrap">
                      <p className="dashboard-omzet-weer-combo-title muted">
                        Kolommen = weekomzet · lijnen = zon (rechtsas), temp en regen (eigen schaal, zie tooltip)
                      </p>
                      <div className="chart-container dashboard-omzet-weer-combo-chart">
                        <ResponsiveContainer width="100%" height={isMobile ? 320 : 380} debounce={50}>
                          <ComposedChart
                            data={comboData}
                            margin={isMobile ? { top: 8, right: 12, left: 4, bottom: 4 } : { top: 12, right: 20, left: 8, bottom: 8 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="label"
                              angle={isMobile ? -90 : -45}
                              textAnchor={isMobile ? 'middle' : 'end'}
                              height={isMobile ? 72 : 90}
                              interval={getXAxisInterval(comboData.length)}
                              tick={{ fontSize: isMobile ? 9 : 11 }}
                            />
                            <YAxis
                              yAxisId="left"
                              orientation="left"
                              tick={{ fontSize: isMobile ? 10 : 11 }}
                              width={isMobile ? 44 : 56}
                              tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                              label={!isMobile ? { value: 'Omzet (€)', angle: -90, position: 'insideLeft' } : undefined}
                            />
                            <YAxis
                              yAxisId="zon"
                              orientation="right"
                              stroke="#f59e0b"
                              tick={{ fontSize: isMobile ? 10 : 11, fill: '#b45309' }}
                              width={isMobile ? 36 : 44}
                              label={!isMobile ? { value: 'Zon (u)', angle: 90, position: 'insideRight', fill: '#b45309' } : undefined}
                            />
                            <YAxis yAxisId="temp" hide />
                            <YAxis yAxisId="regen" hide />
                            <Tooltip
                              content={DashboardClampedTooltip}
                              formatter={(value, name, props) => {
                                const p = props?.payload;
                                const range =
                                  p?.week_start && p?.week_end
                                    ? ` (${new Date(p.week_start).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })}–${new Date(p.week_end).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })})`
                                    : '';
                                const weekTitle = `${p?.label || ''} ${p?.jaar || ''}${range}`;
                                if (name === 'Weekomzet (€)') {
                                  return [
                                    `€ ${Number(value || 0).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`,
                                    `Omzet · ${weekTitle}`,
                                  ];
                                }
                                if (name === 'Zonuren') {
                                  return [`${value ?? '—'} u`, `Zon · ${weekTitle}`];
                                }
                                if (name === 'Temp (°C)') {
                                  return [`${value ?? '—'} °C`, `Temp · ${weekTitle}`];
                                }
                                if (name === 'Regen (nat)') {
                                  return [`${value ?? '—'} mm`, `Regen · ${weekTitle}`];
                                }
                                return [value, name];
                              }}
                            />
                            <Legend
                              wrapperStyle={isMobile ? { fontSize: 10, paddingTop: 4 } : undefined}
                              verticalAlign="bottom"
                            />
                            <Bar
                              yAxisId="left"
                              dataKey="omzet"
                              fill="#ff6b6b"
                              name="Weekomzet (€)"
                              maxBarSize={isMobile ? 14 : 22}
                            />
                            <Line
                              yAxisId="zon"
                              type="monotone"
                              dataKey="zonuren"
                              stroke="#f59e0b"
                              strokeWidth={2}
                              dot={{ r: 2, fill: '#f59e0b' }}
                              connectNulls
                              name="Zonuren"
                            />
                            <Line
                              yAxisId="temp"
                              type="monotone"
                              dataKey="temp"
                              stroke="#2563eb"
                              strokeWidth={2}
                              dot={{ r: 2, fill: '#2563eb' }}
                              connectNulls
                              name="Temp (°C)"
                            />
                            <Line
                              yAxisId="regen"
                              type="monotone"
                              dataKey="regen"
                              stroke="#6366f1"
                              strokeWidth={2}
                              strokeDasharray="5 4"
                              dot={{ r: 2, fill: '#6366f1' }}
                              connectNulls
                              name="Regen (nat)"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : null;
                })()}
                {omzetWeerPrognose.historie.opvallend?.length ? (
                  <div className="dashboard-omzet-weer-highlights">
                    <span className="dashboard-omzet-weer-highlights-label">Opvallend:</span>
                    {omzetWeerPrognose.historie.opvallend.map((w) => (
                      <span key={w.week_start} className="dashboard-omzet-weer-highlight-chip">
                        {w.week_label} {w.jaar}: {w.weer_omschrijving} · € {(w.omzet ?? 0).toLocaleString('nl-NL', { maximumFractionDigits: 0 })} ({fmtOmzetWeerPct(w.omzet_vs_mediaan_pct)} vs mediaan)
                      </span>
                    ))}
                  </div>
                ) : null}
                {omzetWeerPrognose.historie.mediaan_omzet != null ? (
                  <p className="dashboard-omzet-weer-historie-meta muted">
                    Mediaan weekomzet: € {omzetWeerPrognose.historie.mediaan_omzet.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}
                    {omzetWeerPrognose.historie.mediaan_weer?.zonuren != null
                      ? ` · gem. zon ${omzetWeerPrognose.historie.mediaan_weer.zonuren} u`
                      : ''}
                    {omzetWeerPrognose.historie.mediaan_weer?.temp != null
                      ? ` · gem. temp ${omzetWeerPrognose.historie.mediaan_weer.temp} °C`
                      : ''}
                  </p>
                ) : null}
                <div className="dashboard-omzet-weer-table-wrap dashboard-omzet-weer-historie-wrap">
                  <table className="dashboard-omzet-weer-table dashboard-omzet-weer-historie-table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th className="col-num">Omzet</th>
                        <th className="col-num">vs mediaan</th>
                        <th>Weer</th>
                        <th className="col-num">Zon (u)</th>
                        <th className="col-num">Temp °C</th>
                        <th className="col-num">Regen mm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {omzetWeerPrognose.historie.weken.map((w) => (
                        <tr
                          key={w.week_start}
                          className={w.opvallend === 'mooi_weer_hoge_omzet' ? 'dashboard-omzet-weer-historie-row-highlight' : ''}
                        >
                          <td>
                            <strong>{w.week_label}</strong>
                            <span className="dashboard-omzet-weer-datum">
                              {' '}
                              {w.jaar}
                              {w.week_start && w.week_end
                                ? ` (${new Date(w.week_start).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })}–${new Date(w.week_end).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })})`
                                : ''}
                            </span>
                            {w.opvallend ? (
                              <span className="dashboard-omzet-weer-opvallend-badge" title={OMZET_WEER_OPVALLEND_LABELS[w.opvallend]}>
                                {w.opvallend === 'mooi_weer_hoge_omzet' ? '☀' : '•'}
                              </span>
                            ) : null}
                          </td>
                          <td className="col-num">€ {(w.omzet ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={`col-num ${Number(w.omzet_vs_mediaan_pct) > 0 ? 'is-positive' : Number(w.omzet_vs_mediaan_pct) < 0 ? 'is-negative' : ''}`}>
                            {fmtOmzetWeerPct(w.omzet_vs_mediaan_pct)}
                          </td>
                          <td>{w.weer_omschrijving}</td>
                          <td className="col-num">{w.zonuren ?? '—'}</td>
                          <td className="col-num">{w.temp_gem ?? '—'}</td>
                          <td className="col-num">{w.neerslag_mm ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}
            {omzetWeerPrognose.validatie?.length ? (
              <details className="dashboard-berekening-uitleg dashboard-omzet-weer-validatie-details">
                <summary className="dashboard-berekening-summary">
                  Voorspelling vs werkelijkheid ({omzetWeerPrognose.validatie.filter((v) => v.status === 'afgerond').length} afgerond)
                </summary>
                <p className="dashboard-omzet-weer-hint muted">
                  Elke prognose-week wordt opgeslagen bij herberekenen. Na afloop vullen we werkelijke omzet in
                  (zelfde ISO-week als de omzetgrafiek: ma–zo, W01–W53).
                  Omzet vj. = dezelfde ISO-week vorig jaar (vóór verkoopgroei-factor).
                </p>
                <div className="dashboard-omzet-weer-table-wrap">
                  <table className="dashboard-omzet-weer-table dashboard-omzet-weer-validatie-table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th className="col-num">Omzet vj.</th>
                        <th className="col-num">Weerfactor</th>
                        <th className="col-num">Verwacht</th>
                        <th className="col-num">Werkelijk</th>
                        <th className="col-num">Afwijking</th>
                        <th>Voorspeld op</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {omzetWeerPrognose.validatie.map((v) => (
                        <tr key={v.week_start}>
                          <td>
                            <strong>{v.week_label}</strong>
                            <span className="dashboard-omzet-weer-datum"> {v.week_start}</span>
                          </td>
                          <td className="col-num">
                            {v.omzet_vorig_jaar != null
                              ? `€ ${Number(v.omzet_vorig_jaar).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '—'}
                          </td>
                          <td className="col-num">
                            {v.weer_factor != null ? `×${Number(v.weer_factor).toFixed(2)}` : '—'}
                          </td>
                          <td className="col-num">
                            € {(v.verwachte_omzet ?? 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="col-num">
                            {v.werkelijke_omzet != null
                              ? `€ ${v.werkelijke_omzet.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '—'}
                          </td>
                          <td className={`col-num ${Number(v.afwijking_pct) > 0 ? 'is-positive' : Number(v.afwijking_pct) < 0 ? 'is-negative' : ''}`}>
                            {v.afwijking_pct != null ? fmtOmzetWeerPct(v.afwijking_pct) : '—'}
                          </td>
                          <td>
                            {v.eerste_voorspeld_op
                              ? new Date(v.eerste_voorspeld_op).toLocaleDateString('nl-NL')
                              : '—'}
                            {v.laatste_voorspeld_op && v.laatste_voorspeld_op !== v.eerste_voorspeld_op ? (
                              <span className="dashboard-omzet-weer-datum" title="Laatste herberekening vóór of tijdens de week">
                                {' '}
                                (laatst {new Date(v.laatste_voorspeld_op).toLocaleDateString('nl-NL')})
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {v.status === 'afgerond'
                              ? 'Afgerond'
                              : v.status === 'wacht_op_omzet'
                                ? 'Wacht op omzet'
                                : 'Lopend'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}
            <button
              type="button"
              className="magazijn-refresh-btn dashboard-omzet-weer-refresh"
              onClick={fetchOmzetWeerPrognose}
              title="Herberekent uit lokale data; Open-Meteo wordt max. 1× per dag opgehaald"
            >
              Herbereken
            </button>
          </>
        ) : (
          <div className="empty-state">Nog onvoldoende data voor een prognose.</div>
        )}
      </div>

      {/* Herkomst orders (cirkeldiagrammen) */}
      <div className="dashboard-section">
        <h2 className="section-title">
          Herkomst orders
          {compareMode !== 'none' && (
            <span style={{ fontSize: '1rem', color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>
              {compareMode === 'previous' ? '• Huidige vs vorige periode' : '• Huidige periode vs vorig jaar'}
            </span>
          )}
        </h2>
        {loadingSales ? (
          <div className="loading">Laden...</div>
        ) : herkomstData.length > 0 || herkomstCompareData.length > 0 ? (
          (() => {
            const renderHerkomstPie = (data, title) => {
              if (!data || data.length === 0) return <div className="empty-state">Geen herkomstdata</div>;
              const totalOrders = data.reduce((s, d) => s + d.aantal_orders, 0);
              const TOP_N = 7;
              const sorted = [...data].sort((a, b) => b.aantal_orders - a.aantal_orders);
              const top = sorted.slice(0, TOP_N);
              const rest = sorted.slice(TOP_N);
              const overigeCount = rest.reduce((s, d) => s + d.aantal_orders, 0);
              const pieData = overigeCount > 0
                ? [...top, { bron_label: `Overige (${rest.length} bronnen)`, aantal_orders: overigeCount }]
                : top;
              return (
                <div className="herkomst-pie-wrapper">
                  <h3 className="herkomst-pie-title">{title}</h3>
                  <div className="chart-container herkomst-chart">
                    <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="aantal_orders"
                          nameKey="bron_label"
                          cx="50%"
                          cy="50%"
                          outerRadius={isMobile ? 65 : 85}
                          label={isMobile ? false : ({ bron_label, aantal_orders }) => {
                            const pct = totalOrders ? (aantal_orders / totalOrders) * 100 : 0;
                            return pct >= 8 ? `${bron_label}: ${aantal_orders}` : null;
                          }}
                          labelLine={!isMobile}
                        >
                          {pieData.map((_, index) => (
                            <Cell key={index} fill={HERKOMST_COLORS[index % HERKOMST_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={DashboardClampedTooltip} formatter={(value, name) => [`${value} orders`, name]} />
                        {!isMobile && <Legend />}
                      </PieChart>
                    </ResponsiveContainer>
                    <details className="herkomst-bronnen-dropdown">
                      <summary>
                        Alle bronnen ({data.length})
                      </summary>
                      <ul className="herkomst-bronnen-lijst">
                        {data.map((d) => (
                          <li key={d.bron_label}>
                            <span className="herkomst-bron-item">{d.bron_label}: {d.aantal_orders}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                </div>
              );
            };
            const compareLabel = compareMode === 'previous' ? 'Vergelijkingsperiode (vorige periode)' : 'Vergelijkingsperiode (vorig jaar)';
            if (compareMode !== 'none') {
              return (
                <div className="herkomst-two-charts">
                  <div className="herkomst-chart-col">{renderHerkomstPie(herkomstData, 'Huidige periode')}</div>
                  <div className="herkomst-chart-col">{renderHerkomstPie(herkomstCompareData, compareLabel)}</div>
                </div>
              );
            }
            return renderHerkomstPie(herkomstData, 'Geselecteerde periode');
          })()
        ) : (
          <div className="empty-state">Geen herkomstdata voor de geselecteerde periode</div>
        )}
      </div>

      {/* Cumulatieve omzet per week */}
      <div className="dashboard-section">
        <h2 className="section-title">
          Cumulatieve Omzet per Week (Vorig jaar vs {currentYear})
          {compareMode !== 'none' && (
            <span style={{ fontSize: '1rem', color: '#666', fontWeight: 'normal', marginLeft: '0.5rem' }}>
              {compareMode === 'previous' ? '• Vergeleken met vorige periode' : '• Vergeleken met vorig jaar'}
              {compareMode === 'lastyear' && hasForwardPreviewWeeks
                ? ` • ${WEEKLY_CHART_FORWARD_WEEKS} weken vooruit (vorig jaar)`
                : ''}
            </span>
          )}
        </h2>
        <details className="dashboard-berekening-uitleg">
          <summary className="dashboard-berekening-summary">Hoe wordt deze grafiek berekend?</summary>
          <ul>
            <li><strong>Bron:</strong> Tabel <code>verkoopdata</code> (WooCommerce + eventueel andere bronnen). Per verkoopregel: <code>verkoopdatum</code>, <code>totaal_prijs</code>, <code>aantal</code>.</li>
            <li><strong>Per week:</strong> Data wordt gegroepeerd op ISO-week (W01–W53, maandag = weekstart). Per week: <code>totaal_omzet = SUM(totaal_prijs)</code>, <code>week_start = MIN(verkoopdatum)</code>, <code>week_end = MAX(verkoopdatum)</code>.</li>
            <li><strong>Welk jaar:</strong> Zonder datumbereik: <em>Vorig jaar</em> = alle weken van {previousYear}, <em>{currentYear}</em> = alle weken van dit jaar. Met start- en einddatum: alleen weken die in dat bereik vallen.</li>
            <li><strong>Cumulatief:</strong> De staven zijn de <em>lopende som</em>: per week is cumulatieve omzet = som van de weekomzet van alle voorgaande weken in de grafiek + die week. Volgorde is chronologisch (op week_start).</li>
            <li><strong>Controle:</strong> De laatste week in de grafiek zou de totale omzet van het getoonde jaar (of periode) moeten zijn. Klopt dat niet? Controleer dan of Verkoopdata voor dit jaar volledig is gesynchroniseerd (WooCommerce/Bol).</li>
          </ul>
        </details>
        {loadingSales ? (
          <div className="loading">Laden grafiek...</div>
        ) : chartDataCumulative.length > 0 ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={getChartHeight()} debounce={50}>
              <BarChart
                data={chartDataCumulative}
                margin={getChartMargins('cumulative')}
                barCategoryGap={isMobile ? '18%' : '12%'}
                barGap={isMobile ? 3 : 4}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  angle={isMobile ? -90 : -45}
                  textAnchor={isMobile ? 'middle' : 'end'}
                  height={isMobile ? 72 : 100}
                  interval={getXAxisInterval(chartDataCumulative.length)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  label={!isMobile ? { value: 'Omzet (€)', angle: -90, position: 'insideLeft' } : undefined}
                  domain={getCumulativeOmzetDomain(chartDataCumulative)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 40 : 60}
                />
                <Tooltip
                  content={DashboardClampedTooltip}
                  formatter={(value, name) => {
                    const numValue = parseFloat(value || 0);
                    const rounded = Math.round(numValue * 100) / 100;
                    const compareLabel = compareMode === 'previous' ? ' (vorige periode)' : compareMode === 'lastyear' ? ' (vorig jaar)' : '';
                    
                    if (name === 'omzetCumPrev') {
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Cumulatieve omzet ${previousYear}`];
                    }
                    // 2024 vergelijkingsdata niet tonen (omzetCumComparePrev verwijderd)
                    if (name === 'omzetCumCurr') {
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Cumulatieve omzet ${currentYear}`];
                    }
                    if (name === 'omzetCumCompareCurr') {
                      if (compareMode === 'lastyear' && compareYear) {
                        return [`€${rounded.toFixed(2).replace('.', ',')}`, `Cumulatieve omzet ${previousYear} (vergelijkingsperiode)`];
                      }
                      return [`€${rounded.toFixed(2).replace('.', ',')}`, `Cumulatieve omzet ${currentYear}${compareLabel}`];
                    }
                    return value;
                  }}
                  labelFormatter={(label) => {
                    const data = chartDataCumulative.find(d => d.week === label);
                    if (data) {
                      // Gebruik weekStartCurrentYear als primaire bron (huidig jaar), anders weekStart
                      const weekStart = data.weekStartCurrentYear || data.weekStart;
                      const weekEnd = data.weekEndCurrentYear || data.weekEnd;
                      
                      if (weekStart && weekEnd) {
                        return formatWeekInfo(`Week ${label.replace('W', '')}`, weekStart, weekEnd);
                      }
                    }
                    return `Week ${label.replace('W', '')}`;
                  }}
                />
                <Legend 
                  formatter={isMobile ? (value) => {
                    return value.replace(/ \(vergelijkingsperiode\)$/, '').replace(/ \(vorige periode\)$/, '');
                  } : undefined}
                  wrapperStyle={isMobile ? { fontSize: '11px' } : undefined}
                />
                {/* Als vergelijkingsmodus actief is, toon alleen vergelijkingsdata en huidig jaar (niet de dubbele 2025 kolom) */}
                {compareMode === 'none' && (
                  <Bar yAxisId="left" dataKey="omzetCumPrev" fill="#2fb8a8" name={`Cumulatieve omzet ${previousYear}`} maxBarSize={mobileBarMaxSize} />
                )}
                {compareMode !== 'none' && (
                  <Bar 
                    yAxisId="left" 
                    dataKey="omzetCumCompareCurr" 
                    fill="#cc5555" 
                    name={compareMode === 'lastyear' && compareYear
                      ? `Cumulatieve omzet ${previousYear} (vergelijkingsperiode)`
                      : `Cumulatieve omzet ${currentYear}${compareMode === 'previous' ? ' (vorige periode)' : ' (vergelijkingsperiode)'}`
                    }
                    maxBarSize={mobileBarMaxSize}
                  />
                )}
                {/* Huidig jaar altijd als laatste */}
                <Bar yAxisId="left" dataKey="omzetCumCurr" fill="#ff6b6b" name={`Cumulatieve omzet ${currentYear}`} maxBarSize={mobileBarMaxSize} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="empty-state">Geen verkoopdata beschikbaar voor het afgelopen jaar</div>
        )}
      </div>

      <div className="dashboard-section dashboard-cbm-flow-section">
        <h2 className="section-title">CBM planning magazijn en onderweg</h2>
        <details className="dashboard-berekening-uitleg dashboard-cbm-flow-intro-details">
          <summary className="dashboard-berekening-summary">Uitleg CBM-planning</summary>
          <p className="dashboard-cbm-flow-intro">
            Gebruik dit blok om te zien hoeveel volume nu in het magazijn ligt, wat onderweg is via inkooporders en hoeveel CBM er in de gekozen periode naar verwachting verkocht of binnenkomt. Onder de prognosekaarten staat per onderweg inkooporder de verwachte aankomst met CBM en verwachte magazijnbezetting (zelfde logica als de geaggregeerde ETA: cumulatieve instroom in volgorde van datum, minus verwachte verkoop tot die dag).
          </p>
        </details>
        <div className="dashboard-cbm-range-controls">
          <div className="filter-group">
            <label>CBM startdatum</label>
            <input
              type="date"
              value={cbmStartDate}
              onChange={(e) => setCbmStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>CBM einddatum</label>
            <input
              type="date"
              value={cbmEndDate}
              onChange={(e) => setCbmEndDate(e.target.value)}
            />
          </div>
          <button type="button" className="btn btn-primary" onClick={handleApplyCbmRange} disabled={cbmLoading}>
            {cbmLoading ? 'Laden...' : 'CBM-periode toepassen'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setCbmStartDate(defaultCbmDates.start);
              setCbmEndDate(defaultCbmDates.end);
              setCbmLoading(true);
              fetchCbmFlow(defaultCbmDates.start, defaultCbmDates.end, cbmFlowGranularity).finally(() => setCbmLoading(false));
            }}
            disabled={cbmLoading}
          >
            CBM reset
          </button>
        </div>
        <div className="dashboard-cbm-flow-cards">
          {cbmFlowSummaryCards.map((card) => (
            <div key={card.label} className="dashboard-cbm-flow-card">
              <span className="dashboard-cbm-flow-card-label">{card.label}</span>
              <strong className="dashboard-cbm-flow-card-value">{card.value}</strong>
            </div>
          ))}
        </div>
        <div className="dashboard-cbm-flow-meta">
          <span>Periode: {cbmFlow.summary ? `${cbmFlow.start_date || startDate || 'begin'} t/m ${cbmFlow.end_date || endDate || 'heden'}` : `${startDate || 'dit jaar'} t/m ${endDate || 'heden'}`}</span>
          <span>Weergave: {getCbmSectionTitle()}</span>
          <span className={`dashboard-cbm-flow-risk dashboard-cbm-flow-risk-${cbmRiskLabel}`}>Risico-indicatie: {cbmRiskLabel}</span>
        </div>
        <div className="dashboard-cbm-threshold-box">
          <div className="dashboard-cbm-threshold-form">
            <div className="filter-group">
              <label>Jouw maximale CBM opslagcapaciteit magazijn</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={cbmMaxOpslag}
                onChange={(e) => setCbmMaxOpslag(e.target.value)}
                placeholder="bijv. 120,0"
              />
            </div>
            <button type="button" className="btn btn-primary" onClick={saveCbmMaxOpslag} disabled={cbmMaxOpslagSaving}>
              <FiSave /> {cbmMaxOpslagSaving ? 'Opslaan...' : 'Grenswaarde opslaan'}
            </button>
          </div>
          {cbmMaxOpslagNum > 0 && (
            <div className="dashboard-cbm-threshold-results">
              <div className={`dashboard-cbm-threshold-item dashboard-cbm-container-fit ${capaciteitNaOnderweg != null && capaciteitNaOnderweg < 0 ? 'dashboard-cbm-container-fit-no' : 'dashboard-cbm-container-fit-yes'}`}>
                <span className="dashboard-cbm-threshold-label">Passen zendingen onderweg op ETA-prognose?</span>
                <strong>
                  {capaciteitNaOnderweg == null
                    ? '—'
                    : capaciteitNaOnderweg >= 0
                      ? `Ja, nog ${capaciteitNaOnderweg.toFixed(1)} m³ ruimte`
                      : `Nee, ${Math.abs(capaciteitNaOnderweg).toFixed(1)} m³ tekort`}
                </strong>
              </div>
              <div className="dashboard-cbm-threshold-item">
                <span className="dashboard-cbm-threshold-label">Vrije ruimte nu</span>
                <strong className={capaciteitNu != null && capaciteitNu < 0 ? 'is-negative' : ''}>{capaciteitNu != null ? `${capaciteitNu.toFixed(1)} m³` : '—'}</strong>
              </div>
              <div className="dashboard-cbm-threshold-item">
                <span className="dashboard-cbm-threshold-label">
                  Vrije ruimte op zwaarste ETA-moment
                  {cbmFlow.summary?.eta_prognose?.datum ? ` (${cbmFlow.summary.eta_prognose.datum})` : ''}
                </span>
                <strong className={capaciteitNaOnderweg != null && capaciteitNaOnderweg < 0 ? 'is-negative' : ''}>{capaciteitNaOnderweg != null ? `${capaciteitNaOnderweg.toFixed(1)} m³` : '—'}</strong>
              </div>
              <div className="dashboard-cbm-threshold-item">
                <span className="dashboard-cbm-threshold-label">Vrije ruimte over 30 dagen</span>
                <strong className={capaciteit30 != null && capaciteit30 < 0 ? 'is-negative' : ''}>{capaciteit30 != null ? `${capaciteit30.toFixed(1)} m³` : '—'}</strong>
              </div>
              <div className="dashboard-cbm-threshold-item">
                <span className="dashboard-cbm-threshold-label">Vrije ruimte over 60 dagen</span>
                <strong className={capaciteit60 != null && capaciteit60 < 0 ? 'is-negative' : ''}>{capaciteit60 != null ? `${capaciteit60.toFixed(1)} m³` : '—'}</strong>
              </div>
              <div className="dashboard-cbm-threshold-item">
                <span className="dashboard-cbm-threshold-label">Vrije ruimte over 90 dagen</span>
                <strong className={capaciteit90 != null && capaciteit90 < 0 ? 'is-negative' : ''}>{capaciteit90 != null ? `${capaciteit90.toFixed(1)} m³` : '—'}</strong>
              </div>
            </div>
          )}
        </div>
        {cbmFlowInsights.length > 0 && (
          <details className="dashboard-berekening-uitleg dashboard-cbm-flow-insights-details">
            <summary className="dashboard-berekening-summary">Inzichten en interpretatie</summary>
            <div className="dashboard-cbm-flow-insights">
              {cbmFlowInsights.map((insight) => (
                <div key={insight} className="dashboard-cbm-flow-insight">{insight}</div>
              ))}
            </div>
          </details>
        )}
        {Array.isArray(cbmFlow.eta_prognose_per_inkooporder) && cbmFlow.eta_prognose_per_inkooporder.length > 0 && (
          <div className="dashboard-cbm-per-order-wrap">
            <h3 className="dashboard-cbm-per-order-title">Prognose per inkooporder (status onderweg)</h3>
            <details className="dashboard-berekening-uitleg dashboard-cbm-per-order-hint-details">
              <summary className="dashboard-berekening-summary">Hoe lees je deze tabel?</summary>
              <p className="dashboard-cbm-per-order-hint muted">
                Gesorteerd op verwachte leverdatum. <strong>Cumulatief</strong> is de som van het CBM van deze en alle eerdere orders in de lijst (alles dat dan binnen is). <strong>Verwacht magazijn</strong> = huidig magazijn − gemiddelde verkoop/dag × dagen tot ETA + cumulatief.
              </p>
            </details>
            <div className="dashboard-cbm-per-order-cards">
              {cbmFlow.eta_prognose_per_inkooporder.map((row) => {
                const vrije =
                  cbmMaxOpslagNum > 0
                    ? Number((cbmMaxOpslagNum - Number(row.projected_magazijn_cbm || 0)).toFixed(1))
                    : null;
                return (
                  <div key={row.inkooporder_id} className="dashboard-cbm-order-card">
                    <div className="dashboard-cbm-order-card-head">
                      <Link to="/inkooporders" className="dashboard-cbm-order-link">
                        {row.ordernummer || `#${row.inkooporder_id}`}
                      </Link>
                      <span className="dashboard-cbm-order-card-eta">Aankomst: {row.eta || '—'}</span>
                    </div>
                    <dl className="dashboard-cbm-order-card-dl">
                      <dt>Leverancier</dt>
                      <dd>{row.leverancier_naam || '—'}</dd>
                      <dt>Dagen tot ETA</dt>
                      <dd>{row.dagen_tot_eta != null ? row.dagen_tot_eta : '—'}</dd>
                      <dt>CBM order</dt>
                      <dd>{Number(row.cbm_order || 0).toFixed(1)} m³</dd>
                      <dt>CBM cumulatief</dt>
                      <dd>{Number(row.cbm_cumulatief_tot_en_met_eta || 0).toFixed(1)} m³</dd>
                      <dt>Verw. magazijn</dt>
                      <dd>{Number(row.projected_magazijn_cbm || 0).toFixed(1)} m³</dd>
                      {cbmMaxOpslagNum > 0 ? (
                        <>
                          <dt>Vrije ruimte</dt>
                          <dd className={vrije != null && vrije < 0 ? 'is-negative' : ''}>
                            {vrije != null ? `${vrije.toFixed(1)} m³` : '—'}
                          </dd>
                        </>
                      ) : null}
                    </dl>
                  </div>
                );
              })}
            </div>
            <div className="table-container dashboard-cbm-per-order-table-wrap dashboard-cbm-desktop-only">
              <table className="data-table dashboard-cbm-per-order-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Leverancier</th>
                    <th>Verwachte aankomst</th>
                    <th>Dagen</th>
                    <th>CBM order</th>
                    <th>CBM cumulatief</th>
                    <th>Verw. magazijn (m³)</th>
                    {cbmMaxOpslagNum > 0 ? <th>Vrije ruimte (m³)</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {cbmFlow.eta_prognose_per_inkooporder.map((row) => {
                    const vrije =
                      cbmMaxOpslagNum > 0
                        ? Number((cbmMaxOpslagNum - Number(row.projected_magazijn_cbm || 0)).toFixed(1))
                        : null;
                    return (
                      <tr key={row.inkooporder_id}>
                        <td>
                          <Link to="/inkooporders" className="dashboard-cbm-order-link">
                            {row.ordernummer || `#${row.inkooporder_id}`}
                          </Link>
                        </td>
                        <td>{row.leverancier_naam || '—'}</td>
                        <td>{row.eta || '—'}</td>
                        <td>{row.dagen_tot_eta != null ? row.dagen_tot_eta : '—'}</td>
                        <td>{Number(row.cbm_order || 0).toFixed(1)}</td>
                        <td>{Number(row.cbm_cumulatief_tot_en_met_eta || 0).toFixed(1)}</td>
                        <td>{Number(row.projected_magazijn_cbm || 0).toFixed(1)}</td>
                        {cbmMaxOpslagNum > 0 ? (
                          <td className={vrije != null && vrije < 0 ? 'is-negative' : ''}>
                            {vrije != null ? vrije.toFixed(1) : '—'}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {cbmFlow.periods.length > 0 ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={isMobile ? 380 : 420} debounce={50}>
              <ComposedChart data={cbmFlow.periods} margin={getChartMargins('cbm')}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  angle={isMobile ? -90 : -40}
                  textAnchor={isMobile ? 'middle' : 'end'}
                  height={isMobile ? 78 : 90}
                  interval={getXAxisInterval(cbmFlow.periods.length)}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                  yAxisId="left"
                  label={!isMobile ? { value: 'CBM (m³)', angle: -90, position: 'insideLeft' } : undefined}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 44 : 60}
                />
                <Tooltip
                  content={DashboardClampedTooltip}
                  formatter={(value, name) => {
                    if (name === 'verkocht_cbm') return [`${Number(value || 0).toFixed(1)} m³`, 'Verkocht'];
                    if (name === 'inkomend_cbm') return [`${Number(value || 0).toFixed(1)} m³`, 'Inkomend op ETA'];
                    if (name === 'netto_cbm') return [`${Number(value || 0).toFixed(1)} m³`, 'Netto'];
                    return value;
                  }}
                />
                <Legend
                  wrapperStyle={
                    isMobile
                      ? { fontSize: 10, lineHeight: 1.35, paddingTop: 6, marginBottom: -4 }
                      : undefined
                  }
                  verticalAlign="bottom"
                />
                <Bar yAxisId="left" dataKey="verkocht_cbm" fill="#ef4444" name="Verwacht verkocht CBM" maxBarSize={mobileBarMaxSize} />
                <Bar yAxisId="left" dataKey="inkomend_cbm" fill="#14b8a6" name="Inkomend CBM (ETA)" maxBarSize={mobileBarMaxSize} />
                <Line yAxisId="left" type="monotone" dataKey="netto_cbm" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 2 }} name="Netto CBM" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="empty-state">Geen CBM-data beschikbaar voor de gekozen periode.</div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;
